import React, { useState, useEffect } from 'react';
import { authFetch } from '../api/orchestrator';
import { AgentExplainer } from './AgentExplainer';
import { TemplateLibrary } from './TemplateLibrary';
import { AgentExclusionsTab } from './AgentExclusionsTab';

// Use relative URLs in production, localhost in development
const API_BASE = process.env.NODE_ENV === "production" ? "" : "http://localhost:8090";

interface ChatAgent {
  id: string;
  tenant_id: string;
  name: string;
  description?: string;
  avatar_url?: string;
  default_language: string;
  target_audience?: any;
  business_domain?: string;
  supported_channels: string[];
  is_active: boolean;
  created_at: string;
  updated_at: string;
  created_by?: string;
}

interface ChatAgentVersion {
  id: string;
  tenant_id: string;
  chat_agent_id: string;
  version: number;
  stage: 'draft' | 'production' | 'archived';
  system_prompt: string;
  tool_policy: any;
  rag_policy: any;
  safety_policy: any;
  conversation_policy: any;
  temperature?: number | string;  // PostgreSQL numeric returns as string
  changelog?: string;
  llm_provider?: string;
  llm_model?: string;
  reasoning?: boolean | null; // null=auto-detect, true=force enable, false=force disable
  // Old delay settings (kept for backward compatibility)
  min_response_delay_seconds?: number;
  max_response_delay_seconds?: number;
  // New human-like typing settings
  typing_speed_cps?: number | string;  // Characters per second (3.75 = 45 WPM average human)
  typing_burst_enabled?: boolean;
  typing_burst_min?: number;
  typing_burst_max?: number;
  created_by?: string;
  created_at: string;
  promoted_to_production_at?: string;
}

interface AgentFactoryProps {
  tenantId: string;
}

export const AgentFactory: React.FC<AgentFactoryProps> = ({ tenantId }) => {
  const [agents, setAgents] = useState<ChatAgent[]>([]);
  const [selectedAgent, setSelectedAgent] = useState<ChatAgent | null>(null);
  const [versions, setVersions] = useState<ChatAgentVersion[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showVersionModal, setShowVersionModal] = useState(false);
  const [editingVersion, setEditingVersion] = useState<ChatAgentVersion | null>(null);
  const [saveSuccess, setSaveSuccess] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [showCloseConfirmation, setShowCloseConfirmation] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [showExplainer, setShowExplainer] = useState(false);
  const [showTemplateLibrary, setShowTemplateLibrary] = useState(false);
  const [versionModalSource, setVersionModalSource] = useState<{
    mode: 'create' | 'edit';
    sourceVersion?: { version: number; stage: ChatAgentVersion['stage'] };
  }>({ mode: 'create' });
  const [saveNotice, setSaveNotice] = useState<{ type: 'success' | 'error'; message: string } | null>(null);

  // Tool selection state
  const [availableTools, setAvailableTools] = useState<Array<{
    id: string;
    name: string;
    description: string;
    category: string;
    status: string;
  }>>([]);
  const [enabledTools, setEnabledTools] = useState({
    rag_search: false,
    calendar_booking: false,
    crm_leads: false,
    human_handoff: true
  });

  // AI improvement state
  const [aiImproving, setAiImproving] = useState(false);
  const [aiSuggestion, setAiSuggestion] = useState<string | null>(null);
  const [selectedModel, setSelectedModel] = useState('deepseek/deepseek-v3.2');
  const [availableModels, setAvailableModels] = useState<Array<{ id: string; name: string }>>([]);
  const [hasCustomApiKey, setHasCustomApiKey] = useState(false);
  const [hasOpenRouterKey, setHasOpenRouterKey] = useState(false);
  const [checkingApiKey, setCheckingApiKey] = useState(true);

  // Agent testing state (for modal)
  const [testMessages, setTestMessages] = useState<Array<{
    role: 'user' | 'assistant';
    content: string;
    confidence?: number;
    timestamp?: string;
  }>>([]);
  const [testInput, setTestInput] = useState('');
  const [testing, setTesting] = useState(false);
  const [testError, setTestError] = useState<string | null>(null);

  // System Prompt textarea expansion state
  const [promptExpanded, setPromptExpanded] = useState(false);

  // Main page quick test state (separate from modal test state)
  const [mainPageTestMessages, setMainPageTestMessages] = useState<Array<{
    role: 'user' | 'assistant';
    content: string;
    confidence?: number;
    timestamp?: string;
  }>>([]);
  const [mainPageTestInput, setMainPageTestInput] = useState('');
  const [mainPageTesting, setMainPageTesting] = useState(false);
  const [mainPageTestError, setMainPageTestError] = useState<string | null>(null);
  const [selectedTestVersionId, setSelectedTestVersionId] = useState<string | null>(null);

  // Older version display state
  const [selectedOlderVersionId, setSelectedOlderVersionId] = useState<string>('');
  const [retrievedVersionId, setRetrievedVersionId] = useState<string | null>(null);

  // Form state for creating/editing agents
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    default_language: 'en',
    business_domain: '',
    supported_channels: ['whatsapp'] as string[]
  });

  // Form state for creating versions
  const [versionFormData, setVersionFormData] = useState({
    agent_name: 'Ava',
    system_prompt: '',
    changelog: '',
    temperature: 0.05,
    llm_provider: 'openrouter',
    llm_model: 'deepseek/deepseek-v3.2',
    reasoning: null as boolean | null, // Auto-detect (enable for DeepSeek models)
    min_response_delay_seconds: 3,
    max_response_delay_seconds: 7,
    max_questions_per_response: 1,
    strip_markdown_bold: true,
    enable_post_processing: true,
    // New human-like typing settings
    typing_speed_cps: 5,
    typing_burst_enabled: true,
    typing_burst_min: 1,
    typing_burst_max: 3,
    // Client Memory Configuration
    client_memory_config: {
      enabled: true,
      fields: [
        {
          key: 'name',
          label: 'Name',
          enabled: true,
          patterns: ['My name is {name}', 'I am {name}', 'I\'m {name}', 'This is {name}'],
          extraction_type: 'regex'
        },
        {
          key: 'phone',
          label: 'Phone Number',
          enabled: true,
          patterns: ['\\+?\\d{8,15}', '\\d{3}[-.\\s]?\\d{3}[-.\\s]?\\d{4}'],
          extraction_type: 'regex'
        },
        {
          key: 'email',
          label: 'Email Address',
          enabled: true,
          patterns: ['[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\\.[a-zA-Z]{2,}'],
          extraction_type: 'regex'
        }
      ]
    }
  });

  useEffect(() => {
    loadAgents();
    checkCustomApiKey();
    loadTools();
  }, [tenantId]);

  useEffect(() => {
    if (selectedAgent) {
      loadVersions(selectedAgent.id);
      // Reset main page test state when switching agents
      setMainPageTestMessages([]);
      setMainPageTestInput('');
      setMainPageTestError(null);
    }
  }, [selectedAgent]);

  useEffect(() => {
    if (showVersionModal) {
      loadAvailableModels();
    }
  }, [showVersionModal]);

  // ESC key handler for version modal
  useEffect(() => {
    const handleEscKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && showVersionModal) {
        event.preventDefault();
        handleCloseModal();
      }
    };

    if (showVersionModal) {
      document.addEventListener('keydown', handleEscKey);
    }

    return () => {
      document.removeEventListener('keydown', handleEscKey);
    };
  }, [showVersionModal, hasUnsavedChanges]);

  // Track unsaved changes
  useEffect(() => {
    const defaultClientMemoryConfig = {
      enabled: true,
      fields: [
        { key: 'name', label: 'Name', enabled: true, patterns: ['My name is {name}', 'I am {name}', 'I\'m {name}', 'This is {name}'], extraction_type: 'regex' },
        { key: 'phone', label: 'Phone Number', enabled: true, patterns: ['\\+?\\d{8,15}', '\\d{3}[-.\\s]?\\d{3}[-.\\s]?\\d{4}'], extraction_type: 'regex' },
        { key: 'email', label: 'Email Address', enabled: true, patterns: ['[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\\.[a-zA-Z]{2,}'], extraction_type: 'regex' }
      ]
    };

    const hasChanges =
      versionFormData.agent_name !== 'Ava' ||
      versionFormData.system_prompt !== '' ||
      versionFormData.changelog !== '' ||
      versionFormData.llm_provider !== 'openrouter' ||
      versionFormData.llm_model !== 'deepseek/deepseek-v3.2' ||
      versionFormData.reasoning !== null ||
      versionFormData.temperature !== 0.05 ||
      versionFormData.min_response_delay_seconds !== 3 ||
      versionFormData.max_response_delay_seconds !== 7 ||
      versionFormData.max_questions_per_response !== 1 ||
      versionFormData.strip_markdown_bold !== true ||
      versionFormData.enable_post_processing !== true ||
      Number(versionFormData.typing_speed_cps) !== 5 ||
      versionFormData.typing_burst_enabled !== true ||
      versionFormData.typing_burst_min !== 1 ||
      versionFormData.typing_burst_max !== 3 ||
      JSON.stringify(versionFormData.client_memory_config) !== JSON.stringify(defaultClientMemoryConfig);

    setHasUnsavedChanges(hasChanges);
  }, [versionFormData]);

  // Auto-select production version for testing when versions change
  useEffect(() => {
    if (versions.length > 0) {
      // Find production version, or fallback to latest draft
      const productionVersion = versions.find(v => v.stage === 'production');
      const defaultVersion = productionVersion || versions.reduce((latest, v) =>
        v.version > latest.version ? v : latest, versions[0]);
      setSelectedTestVersionId(defaultVersion.id);
    } else {
      setSelectedTestVersionId(null);
    }
  }, [versions]);

  const loadAgents = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await authFetch(`${API_BASE}/api/chat-agents?tenantId=${tenantId}`);
      const data = await response.json();

      if (data.ok) {
        setAgents(data.agents);
        if (data.agents.length > 0 && !selectedAgent) {
          setSelectedAgent(data.agents[0]);
        }
      } else {
        setError(data.error || 'Failed to load agents');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load agents');
    } finally {
      setLoading(false);
    }
  };

  const loadVersions = async (agentId: string) => {
    try {
      const response = await authFetch(`${API_BASE}/api/chat-agents/${agentId}/versions?tenantId=${tenantId}`);
      const data = await response.json();

      if (data.ok) {
        setVersions(data.versions);
      } else {
        console.error('Failed to load versions:', data.error);
      }
    } catch (err) {
      console.error('Failed to load versions:', err);
    }
  };

  const checkCustomApiKey = async () => {
    try {
      setCheckingApiKey(true);
      const response = await authFetch(`${API_BASE}/api/admin/openrouter/check-custom-key?tenantId=${tenantId}`);
      const data = await response.json();
      if (data.ok) {
        setHasCustomApiKey(data.hasCustomKey);
        // If no custom key, check if default key exists
        if (!data.hasCustomKey) {
          setHasOpenRouterKey(data.hasDefaultKey || false);
        } else {
          setHasOpenRouterKey(true);
        }
      }
    } catch (err) {
      console.error('Failed to check custom API key:', err);
    } finally {
      setCheckingApiKey(false);
    }
  };

  const loadAvailableModels = async () => {
    try {
      console.log('🔍 Loading available models from API...');
      const response = await authFetch('https://app.forcemx.io/api/admin/openrouter/models');
      console.log('📡 API response status:', response.status);
      const data = await response.json();
      console.log('📦 API response data:', { ok: data.ok, count: data.count, modelsLength: data.models?.length });

      if (data.ok) {
        console.log('✅ Setting available models:', data.models.length);
        setAvailableModels(data.models);
      } else {
        console.warn('⚠️ API returned ok: false', data);
      }
    } catch (err) {
      console.error('❌ Failed to load available models:', err);
    }
  };

  const loadTools = async () => {
    try {
      const response = await authFetch(`${API_BASE}/api/tools`);
      const data = await response.json();

      if (data.ok) {
        setAvailableTools(data.tools);
      }
    } catch (err) {
      console.error('Failed to load tools:', err);
    }
  };

  const showSaveNotice = (type: 'success' | 'error', message: string) => {
    setSaveNotice({ type, message });
    setTimeout(() => setSaveNotice(null), 3000);
  };

  const parseResponseData = async (response: Response) => {
    const rawText = await response.text();

    if (!rawText) {
      return { ok: response.ok, error: response.ok ? null : 'Empty server response' };
    }

    try {
      return JSON.parse(rawText);
    } catch {
      const compact = rawText.replace(/\s+/g, ' ').trim();
      return {
        ok: false,
        error: compact.slice(0, 300) || `Server returned ${response.status}`
      };
    }
  };

  const handleCreateAgent = async () => {
    try {
      const response = await authFetch(`${API_BASE}/api/chat-agents`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          tenantId,
          ...formData
        })
      });

      const data = await parseResponseData(response);

      if (data.ok) {
        await loadAgents();
        setShowCreateModal(false);
        setFormData({
          name: '',
          description: '',
          default_language: 'en',
          business_domain: '',
          supported_channels: ['whatsapp']
        });
        setSelectedAgent(data.agent);
      } else {
        setError(data.error || 'Failed to create agent');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to create agent');
    }
  };

  const handleCreateVersion = async () => {
    if (!selectedAgent) return;

    const isDirectEdit = versionModalSource.mode === 'edit' && !!editingVersion;

    // Check for OpenRouter API key before creating a new version
    if (!isDirectEdit && !hasOpenRouterKey && checkingApiKey === false) {
      setError('⚠️ No OpenRouter API key configured. Please add an API Key in Admin → Integrations → OpenRouter Settings before creating an agent.');
      return;
    }

    setIsSaving(true);
    setError(null);
    setSaveSuccess(null);

    try {
      const isEditing = isDirectEdit;
      const url = isEditing
        ? `${API_BASE}/api/chat-agents/${selectedAgent.id}/versions/${editingVersion.id}`
        : `${API_BASE}/api/chat-agents/${selectedAgent.id}/versions`;

      const response = await authFetch(url, {
        method: isEditing ? 'PUT' : 'POST',
        body: JSON.stringify({
          tenantId,
          ...versionFormData,
          tool_policy: {
            enabled_tools: enabledTools,
            tool_limits: {
              rag_max_chunks: 8
            },
            handoff_rules: {
              handoff_if_confidence_below: 0.55
            }
          }
        })
      });

      const data = await parseResponseData(response);

      if (data.ok) {
        await loadVersions(selectedAgent.id);
        // Keep modal open for "Update Version" button
        // Clear the unsaved changes state
        setHasUnsavedChanges(false);
        // Show success message
        setSaveSuccess(
          isEditing
            ? '✅ Changes saved successfully!'
            : '✅ Version created successfully!'
        );
        showSaveNotice(
          'success',
          isEditing
            ? 'Changes saved successfully.'
            : 'Version created successfully.'
        );
        // Clear success message after 3 seconds
        setTimeout(() => setSaveSuccess(null), 3000);
      } else {
        showSaveNotice('error', data.error || `Failed to ${isEditing ? 'update' : 'create'} version`);
        setError(data.error || `Failed to ${isEditing ? 'update' : 'create'} version`);
      }
    } catch (err: any) {
      showSaveNotice('error', err.message || `Failed to ${editingVersion ? 'update' : 'create'} version`);
      setError(err.message || `Failed to ${editingVersion ? 'update' : 'create'} version`);
    } finally {
      setIsSaving(false);
    }
  };

  const handleCreateVersionAndExit = async () => {
    if (!selectedAgent) return;

    const isDirectEdit = versionModalSource.mode === 'edit' && !!editingVersion;

    // Check for OpenRouter API key before creating a new version
    if (!isDirectEdit && !hasOpenRouterKey && checkingApiKey === false) {
      setError('⚠️ No OpenRouter API key configured. Please add an API Key in Admin → Integrations → OpenRouter Settings before creating an agent.');
      return;
    }

    setIsSaving(true);
    setError(null);
    setSaveSuccess(null);

    try {
      const isEditing = isDirectEdit;
      const url = isEditing
        ? `${API_BASE}/api/chat-agents/${selectedAgent.id}/versions/${editingVersion.id}`
        : `${API_BASE}/api/chat-agents/${selectedAgent.id}/versions`;

      const response = await authFetch(url, {
        method: isEditing ? 'PUT' : 'POST',
        body: JSON.stringify({
          tenantId,
          ...versionFormData,
          tool_policy: {
            enabled_tools: enabledTools,
            tool_limits: {
              rag_max_chunks: 8
            },
            handoff_rules: {
              handoff_if_confidence_below: 0.55
            }
          }
        })
      });

      const data = await response.json();

      if (data.ok) {
        await loadVersions(selectedAgent.id);
        setHasUnsavedChanges(false);
        setShowVersionModal(false);
        setShowCloseConfirmation(false);
        setEditingVersion(null);
        setVersionModalSource({ mode: 'create' });
        showSaveNotice(
          'success',
          isEditing
            ? 'Changes saved successfully.'
            : 'Version created successfully.'
        );
        setVersionFormData({
          agent_name: 'Ava',
          system_prompt: '',
          changelog: '',
          temperature: 0.05,
          llm_provider: 'openrouter',
          llm_model: 'deepseek/deepseek-v3.2',
          reasoning: null,
          min_response_delay_seconds: 3,
          max_response_delay_seconds: 7,
          max_questions_per_response: 1,
          strip_markdown_bold: true,
          enable_post_processing: true,
    // New human-like typing settings
    typing_speed_cps: 5,
    typing_burst_enabled: true,
    typing_burst_min: 1,
    typing_burst_max: 3,
    // Client Memory Configuration
    client_memory_config: {
      enabled: true,
      fields: [
        { key: 'name', label: 'Name', enabled: true, patterns: ['My name is {name}', 'I am {name}', 'I\'m {name}', 'This is {name}'], extraction_type: 'regex' },
        { key: 'phone', label: 'Phone Number', enabled: true, patterns: ['\\+?\\d{8,15}', '\\d{3}[-.\\s]?\\d{3}[-.\\s]?\\d{4}'], extraction_type: 'regex' },
        { key: 'email', label: 'Email Address', enabled: true, patterns: ['[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\\.[a-zA-Z]{2,}'], extraction_type: 'regex' }
      ]
    }
        });
        setEnabledTools({
          rag_search: false,
          calendar_booking: false,
          crm_leads: false,
          human_handoff: true
        });
      } else {
        showSaveNotice('error', data.error || `Failed to ${isEditing ? 'update' : 'create'} version`);
        setError(data.error || `Failed to ${isEditing ? 'update' : 'create'} version`);
      }
    } catch (err: any) {
      showSaveNotice('error', err.message || `Failed to ${editingVersion ? 'update' : 'create'} version`);
      setError(err.message || `Failed to ${editingVersion ? 'update' : 'create'} version`);
    } finally {
      setIsSaving(false);
    }
  };

  const handleCloseModal = () => {
    if (hasUnsavedChanges) {
      setShowCloseConfirmation(true);
    } else {
      setShowVersionModal(false);
      setShowCloseConfirmation(false);
      setEditingVersion(null);
      setVersionModalSource({ mode: 'create' });
      setError(null);
      setVersionFormData({
        agent_name: 'Ava',
        system_prompt: '',
        changelog: '',
        temperature: 0.05,
        llm_provider: 'openrouter',
        llm_model: 'deepseek/deepseek-v3.2',
        reasoning: null,
        min_response_delay_seconds: 3,
        max_response_delay_seconds: 7,
        max_questions_per_response: 1,
        strip_markdown_bold: true,
        enable_post_processing: true,
    // New human-like typing settings
    typing_speed_cps: 5,
    typing_burst_enabled: true,
    typing_burst_min: 1,
    typing_burst_max: 3,
    // Client Memory Configuration
    client_memory_config: {
      enabled: true,
      fields: [
        { key: 'name', label: 'Name', enabled: true, patterns: ['My name is {name}', 'I am {name}', 'I\'m {name}', 'This is {name}'], extraction_type: 'regex' },
        { key: 'phone', label: 'Phone Number', enabled: true, patterns: ['\\+?\\d{8,15}', '\\d{3}[-.\\s]?\\d{3}[-.\\s]?\\d{4}'], extraction_type: 'regex' },
        { key: 'email', label: 'Email Address', enabled: true, patterns: ['[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\\.[a-zA-Z]{2,}'], extraction_type: 'regex' }
      ]
    }
      });
      setEnabledTools({
        rag_search: false,
        calendar_booking: false,
        crm_leads: false,
        human_handoff: true
      });
    }
  };

  const handleSaveAsDraft = async () => {
    await handleCreateVersion();
    setShowCloseConfirmation(false);
  };

  const handleDiscardChanges = () => {
    setShowVersionModal(false);
    setShowCloseConfirmation(false);
    setEditingVersion(null);
    setVersionModalSource({ mode: 'create' });
    setError(null);
    setVersionFormData({
      agent_name: 'Ava',
      system_prompt: '',
      changelog: '',
      temperature: 0.05,
      llm_provider: 'openrouter',
      llm_model: 'deepseek/deepseek-v3.2',
      reasoning: null,
      min_response_delay_seconds: 3,
      max_response_delay_seconds: 7,
      max_questions_per_response: 1,
      strip_markdown_bold: true,
      enable_post_processing: true,
    // New human-like typing settings
    typing_speed_cps: 5,
    typing_burst_enabled: true,
    typing_burst_min: 1,
    typing_burst_max: 3,
    // Client Memory Configuration
    client_memory_config: {
      enabled: true,
      fields: [
        { key: 'name', label: 'Name', enabled: true, patterns: ['My name is {name}', 'I am {name}', 'I\'m {name}', 'This is {name}'], extraction_type: 'regex' },
        { key: 'phone', label: 'Phone Number', enabled: true, patterns: ['\\+?\\d{8,15}', '\\d{3}[-.\\s]?\\d{3}[-.\\s]?\\d{4}'], extraction_type: 'regex' },
        { key: 'email', label: 'Email Address', enabled: true, patterns: ['[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\\.[a-zA-Z]{2,}'], extraction_type: 'regex' }
      ]
    }
    });
    setEnabledTools({
      rag_search: false,
      calendar_booking: false,
      crm_leads: false,
      human_handoff: true
    });
  };

  const handleCopyToNewVersion = (version: ChatAgentVersion) => {
    // Copy version settings to create a new version
    setError(null);
    setSaveSuccess(null);
    setEditingVersion(null); // Not editing, creating new
    setVersionModalSource({
      mode: 'create',
      sourceVersion: { version: version.version, stage: version.stage }
    });

    setVersionFormData({
      agent_name: (version as any).agent_name || 'Ava',
      system_prompt: version.system_prompt || '',
      changelog: `Based on version ${version.version} (${version.stage}) - ${version.changelog || 'No changelog'}`,
      temperature: version.temperature !== undefined && version.temperature !== null ? Number(version.temperature) : 0.05,
      llm_provider: version.llm_provider || 'openrouter',
      llm_model: version.llm_model !== undefined && version.llm_model !== null ? version.llm_model : 'deepseek/deepseek-v3.2',
      reasoning: version.reasoning !== undefined ? version.reasoning : null,
      min_response_delay_seconds: version.min_response_delay_seconds !== undefined ? version.min_response_delay_seconds : 3,
      max_response_delay_seconds: version.max_response_delay_seconds !== undefined ? version.max_response_delay_seconds : 7,
      max_questions_per_response: (version as any).max_questions_per_response !== undefined ? (version as any).max_questions_per_response : 1,
      strip_markdown_bold: (version as any).strip_markdown_bold !== undefined ? (version as any).strip_markdown_bold : true,
      enable_post_processing: (version as any).enable_post_processing !== undefined ? (version as any).enable_post_processing : true,
      typing_speed_cps: (version as any).typing_speed_cps !== undefined ? Number(version.typing_speed_cps) : 5,
      typing_burst_enabled: (version as any).typing_burst_enabled !== undefined ? (version as any).typing_burst_enabled : true,
      typing_burst_min: (version as any).typing_burst_min !== undefined ? (version as any).typing_burst_min : 1,
      typing_burst_max: (version as any).typing_burst_max !== undefined ? (version as any).typing_burst_max : 3,
      client_memory_config: (version as any).client_memory_config || {
        enabled: true,
        fields: [
          { key: 'name', label: 'Name', enabled: true, patterns: ['My name is {name}', 'I am {name}', 'I\'m {name}', 'This is {name}'], extraction_type: 'regex' },
          { key: 'phone', label: 'Phone Number', enabled: true, patterns: ['\\+?\\d{8,15}', '\\d{3}[-.\\s]?\\d{3}[-.\\s]?\\d{4}'], extraction_type: 'regex' },
          { key: 'email', label: 'Email Address', enabled: true, patterns: ['[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\\.[a-zA-Z]{2,}'], extraction_type: 'regex' }
        ]
      }
    });

    // Load tools configuration
    const toolPolicy = version.tool_policy ? JSON.parse(version.tool_policy as any) : {};
    setEnabledTools({
      rag_search: toolPolicy.rag_search === true,
      calendar_booking: toolPolicy.calendar_booking === true,
      crm_leads: toolPolicy.crm_leads === true,
      human_handoff: toolPolicy.human_handoff !== false
    });

    // Show version modal to create new version
    setShowVersionModal(true);
  };

  const handleEditVersion = (version: ChatAgentVersion) => {
    // Clear any previous messages
    setError(null);
    setSaveSuccess(null);
    setEditingVersion(version);
    setVersionModalSource({ mode: 'edit' });
    setVersionFormData({
      agent_name: (version as any).agent_name || 'Ava',
      system_prompt: version.system_prompt || '',
      changelog: version.changelog || '',
      temperature: version.temperature !== undefined && version.temperature !== null ? Number(version.temperature) : 0.05,
      llm_provider: version.llm_provider || 'openrouter',
      llm_model: version.llm_model !== undefined && version.llm_model !== null ? version.llm_model : 'deepseek/deepseek-v3.2',
      reasoning: version.reasoning !== undefined ? version.reasoning : null,
      min_response_delay_seconds: version.min_response_delay_seconds !== undefined ? version.min_response_delay_seconds : 3,
      max_response_delay_seconds: version.max_response_delay_seconds !== undefined ? version.max_response_delay_seconds : 7,
      max_questions_per_response: (version as any).max_questions_per_response !== undefined ? (version as any).max_questions_per_response : 1,
      strip_markdown_bold: (version as any).strip_markdown_bold !== undefined ? (version as any).strip_markdown_bold : true,
      enable_post_processing: (version as any).enable_post_processing !== undefined ? (version as any).enable_post_processing : true,
      typing_speed_cps: (version as any).typing_speed_cps !== undefined ? Number(version.typing_speed_cps) : 5,
      typing_burst_enabled: (version as any).typing_burst_enabled !== undefined ? (version as any).typing_burst_enabled : true,
      typing_burst_min: (version as any).typing_burst_min !== undefined ? (version as any).typing_burst_min : 1,
      typing_burst_max: (version as any).typing_burst_max !== undefined ? (version as any).typing_burst_max : 3,
      client_memory_config: (version as any).client_memory_config || {
        enabled: true,
        fields: [
          { key: 'name', label: 'Name', enabled: true, patterns: ['My name is {name}', 'I am {name}', 'I\'m {name}', 'This is {name}'], extraction_type: 'regex' },
          { key: 'phone', label: 'Phone Number', enabled: true, patterns: ['\\+?\\d{8,15}', '\\d{3}[-.\\s]?\\d{3}[-.\\s]?\\d{4}'], extraction_type: 'regex' },
          { key: 'email', label: 'Email Address', enabled: true, patterns: ['[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\\.[a-zA-Z]{2,}'], extraction_type: 'regex' }
        ]
      }
    });

    // Populate enabled tools from version tool_policy
    if (version.tool_policy && typeof version.tool_policy === 'object' && 'enabled_tools' in version.tool_policy) {
      setEnabledTools(version.tool_policy.enabled_tools as any);
    } else {
      // Default values
      setEnabledTools({
        rag_search: false,
        calendar_booking: false,
        crm_leads: false,
        human_handoff: true
      });
    }

    // Reset test state when opening modal
    setTestMessages([]);
    setTestInput('');
    setTestError(null);

    setShowVersionModal(true);
  };

  const handleImprovePrompt = async () => {
    if (!versionFormData.system_prompt) {
      setError('Please enter a system prompt first');
      return;
    }

    setAiImproving(true);
    setError(null);

    try {
      // Use backend proxy for API call to handle custom keys
      const response = await authFetch(`${API_BASE}/api/admin/openrouter/improve-prompt`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          tenantId,
          model: selectedModel,
          prompt: versionFormData.system_prompt
        })
      });

      if (!response.ok) {
        throw new Error(`API error: ${response.statusText}`);
      }

      const data = await response.json();

      if (data.ok && data.improvedPrompt) {
        setAiSuggestion(data.improvedPrompt);
      } else {
        throw new Error(data.error || 'No suggestion returned from AI');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to improve prompt with AI');
    } finally {
      setAiImproving(false);
    }
  };

  const handleAcceptSuggestion = () => {
    if (aiSuggestion) {
      setVersionFormData({ ...versionFormData, system_prompt: aiSuggestion });
      setAiSuggestion(null);
    }
  };

  const handleRejectSuggestion = () => {
    setAiSuggestion(null);
  };

  const handlePromoteVersion = async (versionId: string) => {
    if (!selectedAgent) return;

    if (!confirm('Are you sure you want to promote this version? This will move it to the next stage.')) {
      return;
    }

    try {
      const response = await authFetch(`${API_BASE}/api/chat-agents/${selectedAgent.id}/versions/${versionId}/promote`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ tenantId })
      });

      const data = await response.json();

      if (data.ok) {
        await loadVersions(selectedAgent.id);
        alert(data.message);
      } else {
        setError(data.error || 'Failed to promote version');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to promote version');
    }
  };

  const handleDeleteVersion = async (versionId: string, versionNumber: number) => {
    if (!selectedAgent) return;

    if (!confirm(`Are you sure you want to permanently delete Version ${versionNumber}? This action cannot be undone.`)) {
      return;
    }

    try {
      const response = await authFetch(`${API_BASE}/api/chat-agents/${selectedAgent.id}/versions/${versionId}`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ tenantId })
      });

      const data = await response.json();

      if (data.ok) {
        await loadVersions(selectedAgent.id);
        // Clear retrieved version if it was the deleted one
        if (retrievedVersionId === versionId) {
          setRetrievedVersionId(null);
          setSelectedOlderVersionId('');
        }
      } else {
        setError(data.error || 'Failed to delete version');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to delete version');
    }
  };

  const handleSelectTemplate = (template: any) => {
    // Pre-fill the version form with template data
    setVersionFormData({
      agent_name: 'Ava',
      system_prompt: template.system_prompt,
      changelog: `Created from template: ${template.name}`,
      temperature: template.temperature,
      llm_provider: 'openrouter',
      llm_model: 'deepseek/deepseek-v3.2',
      reasoning: null,
      min_response_delay_seconds: 3,
      max_response_delay_seconds: 7,
      max_questions_per_response: 1,
      strip_markdown_bold: true,
      enable_post_processing: true,
    // New human-like typing settings
    typing_speed_cps: 5,
    typing_burst_enabled: true,
    typing_burst_min: 1,
    typing_burst_max: 3,
    // Client Memory Configuration
    client_memory_config: {
      enabled: true,
      fields: [
        { key: 'name', label: 'Name', enabled: true, patterns: ['My name is {name}', 'I am {name}', 'I\'m {name}', 'This is {name}'], extraction_type: 'regex' },
        { key: 'phone', label: 'Phone Number', enabled: true, patterns: ['\\+?\\d{8,15}', '\\d{3}[-.\\s]?\\d{3}[-.\\s]?\\d{4}'], extraction_type: 'regex' },
        { key: 'email', label: 'Email Address', enabled: true, patterns: ['[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\\.[a-zA-Z]{2,}'], extraction_type: 'regex' }
      ]
    }
    });

    // Set default tools for templates (RAG + Human Handoff)
    setEnabledTools({
      rag_search: true,  // Templates typically use knowledge base
      calendar_booking: false,
      crm_leads: false,
      human_handoff: true  // Always allow escalation
    });

    // Close template library and open version creation modal
    setShowTemplateLibrary(false);
    // Reset test state when opening modal
    setTestMessages([]);
    setTestInput('');
    setTestError(null);
    setShowVersionModal(true);
  };

  const handleOpenCreateVersionModal = () => {
    // Reset form data for new version
    setVersionModalSource({ mode: 'create' });
    setVersionFormData({
      agent_name: 'Ava',
      system_prompt: '',
      changelog: '',
      temperature: 0.05,
      llm_provider: 'openrouter',
      llm_model: 'deepseek/deepseek-v3.2',
      reasoning: null,
      min_response_delay_seconds: 3,
      max_response_delay_seconds: 7,
      max_questions_per_response: 1,
      strip_markdown_bold: true,
      enable_post_processing: true,
    // New human-like typing settings
    typing_speed_cps: 5,
    typing_burst_enabled: true,
    typing_burst_min: 1,
    typing_burst_max: 3,
    // Client Memory Configuration
    client_memory_config: {
      enabled: true,
      fields: [
        { key: 'name', label: 'Name', enabled: true, patterns: ['My name is {name}', 'I am {name}', 'I\'m {name}', 'This is {name}'], extraction_type: 'regex' },
        { key: 'phone', label: 'Phone Number', enabled: true, patterns: ['\\+?\\d{8,15}', '\\d{3}[-.\\s]?\\d{3}[-.\\s]?\\d{4}'], extraction_type: 'regex' },
        { key: 'email', label: 'Email Address', enabled: true, patterns: ['[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\\.[a-zA-Z]{2,}'], extraction_type: 'regex' }
      ]
    }
    });
    setEnabledTools({
      rag_search: false,
      calendar_booking: false,
      crm_leads: false,
      human_handoff: true
    });
    setEditingVersion(null);
    // Reset test state when opening modal
    setTestMessages([]);
    setTestInput('');
    setTestError(null);
    setShowVersionModal(true);
  };

  const handleTestAgent = async () => {
    if (!testInput.trim()) return;

    // Add user message to history
    const userMessage = testInput.trim();
    setTestMessages(prev => [...prev, {
      role: 'user',
      content: userMessage,
      timestamp: new Date().toISOString()
    }]);
    setTestInput('');
    setTesting(true);
    setTestError(null);

    try {
      // Format conversation history for context (last 50 messages)
      const conversationHistory = testMessages.slice(-50).map(msg => ({
        role: msg.role,
        content: msg.content
      }));

      // Get the current editing version's ID (if editing) or use a test with the form data
      // For testing purposes, we'll use a temporary test endpoint that accepts the config directly
      const response = await authFetch(`${API_BASE}/api/agent-testing/quick-test`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          tenant_id: tenantId,
          message: userMessage,
          conversation_history: conversationHistory,
          // If editing, use the version ID, otherwise we'll test with the current form config
          ...(editingVersion ? { agent_version_id: editingVersion.id } : {
            // Create a temp config from form data
            config: {
              agent_name: versionFormData.agent_name,
              system_prompt: versionFormData.system_prompt,
              temperature: versionFormData.temperature,
              llm_provider: versionFormData.llm_provider,
              llm_model: versionFormData.llm_model,
              max_questions_per_response: versionFormData.max_questions_per_response,
              strip_markdown_bold: versionFormData.strip_markdown_bold,
              enable_post_processing: versionFormData.enable_post_processing,
              tool_policy: {
                enabled_tools: enabledTools
              }
            }
          })
        })
      });

      const data = await response.json();

      if (data.ok) {
        // Check if response has typing burst chunks
        if (data._typing_burst && data._typing_burst.chunks.length > 1) {
          const chunks = data._typing_burst.chunks;
          const perChunkDelay = data._typing_burst.per_chunk_delay_ms || 1000;

          // Add each chunk with per-chunk delay (including first chunk)
          // First chunk: wait perChunkDelay before showing
          // Subsequent chunks: each appears perChunkDelay after previous
          for (let i = 0; i < chunks.length; i++) {
            setTimeout(() => {
              setTestMessages(prev => [...prev, {
                role: 'assistant',
                content: typeof chunks[i] === 'string' ? chunks[i] : chunks[i].text,  // Handle both string chunks and object chunks
                confidence: data.confidence,
                timestamp: new Date().toISOString()
              }]);
            }, perChunkDelay * (i + 1)); // +1 because first chunk also has initial delay
          }
        } else {
          // Single response (no typing burst)
          setTestMessages(prev => [...prev, {
            role: 'assistant',
            content: data.response,
            confidence: data.confidence,
            timestamp: new Date().toISOString()
          }]);
        }
      } else {
        // If version not found (new version not created yet), test with current form data
        if (data.error?.includes('not found') && !editingVersion) {
          // Test using the LLM service directly with current form config
          const llmResponse = await authFetch(`${API_BASE}/api/admin/openrouter/chat`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              messages: [
                {
                  role: 'system',
                  content: `${versionFormData.system_prompt}

IMPORTANT: You must provide a confidence score (0.0 to 1.0) for your response.
After your response, on a new line, provide: Confidence: [0.0-1.0]`
                },
                { role: 'user', content: userMessage }
              ],
              model: versionFormData.llm_model || 'deepseek/deepseek-v3.2',
              temperature: versionFormData.temperature || 0.05
            })
          });

          const llmData = await llmResponse.json();

          if (llmData.ok) {
            const text = llmData.response || llmData.text || 'No response';
            let confidence = 0.5;
            let responseText = text;

            const confidenceMatch = text.match(/Confidence:\s*(0?\.\d+|1\.0|0)/i);
            if (confidenceMatch) {
              confidence = parseFloat(confidenceMatch[1]);
              responseText = text.replace(/Confidence:\s*(0?\.\d+|1\.0|0)/i, '').trim();
            }

            setTestMessages(prev => [...prev, {
              role: 'assistant',
              content: responseText,
              confidence: confidence,
              timestamp: new Date().toISOString()
            }]);
          } else {
            setTestError(llmData.error || 'Failed to get response from LLM');
          }
        } else {
          setTestError(data.error || 'Failed to test agent');
        }
      }
    } catch (err: any) {
      setTestError(err.message || 'Failed to test agent');
    } finally {
      setTesting(false);
    }
  };

  const handleMainPageTest = async () => {
    if (!mainPageTestInput.trim() || !selectedAgent || !selectedTestVersionId) return;

    // Find the selected version
    const versionToTest = versions.find(v => v.id === selectedTestVersionId);

    if (!versionToTest) {
      setMainPageTestError('Selected version not found. Please select a version to test.');
      return;
    }

    // Add user message to history
    const userMessage = mainPageTestInput.trim();
    setMainPageTestMessages(prev => [...prev, {
      role: 'user',
      content: userMessage,
      timestamp: new Date().toISOString()
    }]);
    setMainPageTestInput('');
    setMainPageTesting(true);
    setMainPageTestError(null);

    try {
      // Format conversation history for context (last 50 messages)
      const conversationHistory = mainPageTestMessages.slice(-50).map(msg => ({
        role: msg.role,
        content: msg.content
      }));

      const response = await authFetch(`${API_BASE}/api/agent-testing/quick-test`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          agent_version_id: versionToTest.id,
          message: userMessage,
          conversation_history: conversationHistory,
          tenant_id: tenantId
        })
      });

      const data = await response.json();

      if (data.ok) {
        // Check if response has typing burst chunks
        if (data._typing_burst && data._typing_burst.chunks.length > 1) {
          const chunks = data._typing_burst.chunks;
          const perChunkDelay = data._typing_burst.per_chunk_delay_ms || 1000;

          // Add each chunk with per-chunk delay (including first chunk)
          // First chunk: wait perChunkDelay before showing
          // Subsequent chunks: each appears perChunkDelay after previous
          for (let i = 0; i < chunks.length; i++) {
            setTimeout(() => {
              setMainPageTestMessages(prev => [...prev, {
                role: 'assistant',
                content: typeof chunks[i] === 'string' ? chunks[i] : chunks[i].text,  // Handle both string chunks and object chunks
                confidence: data.confidence,
                timestamp: new Date().toISOString()
              }]);
            }, perChunkDelay * (i + 1)); // +1 because first chunk also has initial delay
          }
        } else {
          // Single response (no typing burst)
          setMainPageTestMessages(prev => [...prev, {
            role: 'assistant',
            content: data.response,
            confidence: data.confidence,
            timestamp: new Date().toISOString()
          }]);
        }
      } else {
        setMainPageTestError(data.error || 'Failed to test agent');
      }
    } catch (err: any) {
      setMainPageTestError(err.message || 'Failed to test agent');
    } finally {
      setMainPageTesting(false);
    }
  };

  const getStageColor = (stage: string) => {
    switch (stage) {
      case 'draft': return '#94a3b8';
      case 'production': return '#10b981';
      case 'archived': return '#6b7280';
      default: return '#94a3b8';
    }
  };

  const getStageIcon = (stage: string) => {
    switch (stage) {
      case 'draft': return '📝';
      case 'production': return '🚀';
      case 'archived': return '📦';
      default: return '📝';
    }
  };

  if (loading) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center' }}>
        <div>Loading agents...</div>
      </div>
    );
  }

  return (
    <>
      {saveNotice && (
        <div style={{
          position: 'fixed',
          top: '20px',
          right: '20px',
          zIndex: 2000,
          background: saveNotice.type === 'success' ? '#065f46' : '#991b1b',
          color: 'white',
          padding: '0.9rem 1.1rem',
          borderRadius: '10px',
          boxShadow: '0 10px 25px rgba(0,0,0,0.15)',
          fontSize: '0.9rem',
          fontWeight: '600'
        }}>
          {saveNotice.type === 'success' ? '✅ ' : '⚠️ '}
          {saveNotice.message}
        </div>
      )}
      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
      <div style={{ display: 'flex', height: '100%', background: '#f9fafb' }}>
      {/* Agents List Sidebar */}
      <div style={{
        width: '300px',
        borderRight: '1px solid #e5e7eb',
        background: '#fff',
        display: 'flex',
        flexDirection: 'column'
      }}>
        <div style={{ padding: '1rem', borderBottom: '1px solid #e5e7eb' }}>
          <button
            onClick={() => setShowCreateModal(true)}
            style={{
              width: '100%',
              padding: '0.75rem',
              background: '#f97316',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              fontSize: '0.9rem',
              fontWeight: '600',
              cursor: 'pointer',
              marginBottom: '0.75rem'
            }}
          >
            Create Chat Agent
          </button>

          <button
            onClick={() => { setShowTemplateLibrary(true); setShowExplainer(false); }}
            style={{
              width: '100%',
              padding: '0.75rem',
              background: '#fff',
              color: '#f97316',
              border: '2px solid #f97316',
              borderRadius: '8px',
              fontSize: '0.9rem',
              fontWeight: '600',
              cursor: 'pointer',
              marginBottom: '0.5rem'
            }}
          >
            📚 Template Library
          </button>

          <button
            onClick={() => { setShowExplainer(true); setShowTemplateLibrary(false); }}
            style={{
              width: '100%',
              padding: '0.75rem',
              background: '#fff',
              color: '#6b7280',
              border: '1px solid #d1d5db',
              borderRadius: '8px',
              fontSize: '0.9rem',
              fontWeight: '600',
              cursor: 'pointer'
            }}
          >
            📖 How AI Agents Work
          </button>
        </div>

        <div style={{ flex: 1, overflowY: 'auto' }}>
          {agents.map(agent => (
            <div
              key={agent.id}
              onClick={() => setSelectedAgent(agent)}
              style={{
                padding: '1rem',
                borderBottom: '1px solid #f3f4f6',
                cursor: 'pointer',
                background: selectedAgent?.id === agent.id ? '#fef3c7' : 'transparent',
                borderLeft: selectedAgent?.id === agent.id ? '3px solid #f97316' : '3px solid transparent'
              }}
            >
              <div style={{ fontWeight: '600', color: '#1f2937', marginBottom: '0.25rem' }}>
                {agent.name}
              </div>
              <div style={{ fontSize: '0.85rem', color: '#6b7280', marginBottom: '0.5rem' }}>
                {agent.description || 'No description'}
              </div>
              <div style={{ fontSize: '0.75rem', color: '#9ca3af' }}>
                {agent.business_domain || 'General'} • {agent.default_language.toUpperCase()}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Main Content Area */}
      <div style={{ flex: 1, overflowY: 'auto' }}>
        {showExplainer ? (
          <AgentExplainer />
        ) : showTemplateLibrary ? (
          <TemplateLibrary onSelectTemplate={handleSelectTemplate} />
        ) : selectedAgent ? (
          <div style={{ padding: '2rem' }}>
            {/* Agent Header */}
            <div style={{
              background: '#fff',
              padding: '1.5rem',
              borderRadius: '12px',
              marginBottom: '1.5rem',
              boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                <div>
                  <h2 style={{ margin: '0 0 0.5rem 0', fontSize: '1.5rem', color: '#1f2937' }}>
                    {selectedAgent.name}
                  </h2>
                  <p style={{ margin: '0 0 1rem 0', color: '#6b7280' }}>
                    {selectedAgent.description || 'No description provided'}
                  </p>
                  <div style={{ display: 'flex', gap: '1rem', fontSize: '0.85rem' }}>
                    <span><strong>Domain:</strong> {selectedAgent.business_domain || 'General'}</span>
                    <span><strong>Language:</strong> {selectedAgent.default_language.toUpperCase()}</span>
                    <span><strong>Channels:</strong> {selectedAgent.supported_channels.join(', ')}</span>
                  </div>
                </div>
                <div style={{
                  padding: '0.5rem 1rem',
                  background: selectedAgent.is_active ? '#d1fae5' : '#fee2e2',
                  color: selectedAgent.is_active ? '#065f46' : '#991b1b',
                  borderRadius: '6px',
                  fontSize: '0.85rem',
                  fontWeight: '600'
                }}>
                  {selectedAgent.is_active ? 'Active' : 'Inactive'}
                </div>
              </div>
            </div>

            {/* Chat Exclusions Section */}
            {selectedAgent && (
              <AgentExclusionsTab
                agentId={selectedAgent.id}
                copySourceAgentId={
                  selectedAgent.name === 'Acknowledgement Agent'
                    ? agents.find((agent) => agent.name === 'WhatsApp Sales Assistant')?.id
                    : undefined
                }
                copySourceAgentName={
                  selectedAgent.name === 'Acknowledgement Agent'
                    ? 'WhatsApp Sales Assistant'
                    : undefined
                }
              />
            )}

            {/* Versions Section */}
            <div style={{
              background: '#fff',
              padding: '1.5rem',
              borderRadius: '12px',
              boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                <h3 style={{ margin: 0, fontSize: '1.25rem', color: '#1f2937' }}>
                  Versions
                </h3>
                <button
                  onClick={handleOpenCreateVersionModal}
                  style={{
                    padding: '0.5rem 1rem',
                    background: '#f97316',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    fontSize: '0.85rem',
                    fontWeight: '600',
                    cursor: 'pointer'
                  }}
                >
                  + Create Version
                </button>
              </div>

              {/* Dropdown to select older versions */}
              {(() => {
                const productionVersion = versions.find(v => v.stage === 'production');
                const latestDraft = versions.find(v => v.stage === 'draft');
                const olderVersions = versions.filter(v =>
                  v.id !== productionVersion?.id && v.id !== latestDraft?.id
                );

                return (
                  <>
                    {olderVersions.length > 0 && (
                      <div style={{
                        padding: '1rem',
                        background: '#f9fafb',
                        borderRadius: '8px',
                        border: '1px solid #e5e7eb',
                        marginBottom: '1rem'
                      }}>
                        <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
                          <select
                            value={selectedOlderVersionId}
                            onChange={(e) => setSelectedOlderVersionId(e.target.value)}
                            style={{
                              flex: 1,
                              padding: '0.75rem',
                              border: '1px solid #d1d5db',
                              borderRadius: '6px',
                              fontSize: '0.9rem',
                              background: 'white'
                            }}
                          >
                            <option value="">Select an older version to view...</option>
                            {olderVersions.map(v => (
                              <option key={v.id} value={v.id}>
                                Version {v.version} - {v.stage.toUpperCase()} - {new Date(v.created_at).toLocaleDateString()}
                              </option>
                            ))}
                          </select>
                          <button
                            onClick={() => setRetrievedVersionId(selectedOlderVersionId)}
                            disabled={!selectedOlderVersionId}
                            style={{
                              padding: '0.75rem 1.5rem',
                              background: selectedOlderVersionId ? '#3b82f6' : '#9ca3af',
                              color: 'white',
                              border: 'none',
                              borderRadius: '6px',
                              fontSize: '0.9rem',
                              fontWeight: '600',
                              cursor: selectedOlderVersionId ? 'pointer' : 'not-allowed'
                            }}
                          >
                            📥 Retrieve Version
                          </button>
                        </div>
                      </div>
                    )}
                  </>
                );
              })()}

              <div style={{ display: 'grid', gap: '1rem' }}>
                {versions.filter(version => {
                  // Always show production version
                  if (version.stage === 'production') return true;

                  // Always show latest draft
                  const latestDraft = versions.find(v => v.stage === 'draft');
                  if (version.id === latestDraft?.id) return true;

                  // Show retrieved older version
                  if (version.id === retrievedVersionId) return true;

                  return false;
                }).map(version => (
                  <div
                    key={version.id}
                    style={{
                      padding: '1rem',
                      border: '1px solid #e5e7eb',
                      borderRadius: '8px',
                      background: version.id === retrievedVersionId ? '#fef3c7' : '#fafafa'
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                        <span style={{ fontSize: '1.5rem' }}>{getStageIcon(version.stage)}</span>
                        <div>
                          <div style={{ fontWeight: '600', color: '#1f2937' }}>
                            Version {version.version}
                          </div>
                          <div style={{ fontSize: '0.85rem', color: getStageColor(version.stage), fontWeight: '600' }}>
                            {version.stage.toUpperCase()}
                          </div>
                        </div>
                      </div>
                      <div style={{ display: 'flex', gap: '0.5rem' }}>
                        {version.stage !== 'archived' && (
                          <button
                            onClick={() => handleEditVersion(version)}
                            style={{
                              padding: '0.5rem 1rem',
                              background: '#6b7280',
                              color: 'white',
                              border: 'none',
                              borderRadius: '6px',
                              fontSize: '0.85rem',
                              fontWeight: '600',
                              cursor: 'pointer'
                            }}
                            title={version.stage === 'draft' ? 'Edit this draft version' : 'Edit this version'}
                          >
                            ✏️ Edit
                          </button>
                        )}
                        {version.stage !== 'draft' && (
                          <button
                            onClick={() => handleCopyToNewVersion(version)}
                            style={{
                              padding: '0.5rem 1rem',
                              background: '#f59e0b',
                              color: 'white',
                              border: 'none',
                              borderRadius: '6px',
                              fontSize: '0.85rem',
                              fontWeight: '600',
                              cursor: 'pointer'
                            }}
                            title="Create a new draft version based on this selected version"
                          >
                            ➕ Create New Version
                          </button>
                        )}
                        {version.stage !== 'production' && version.stage !== 'archived' && (
                          <button
                            onClick={() => handlePromoteVersion(version.id)}
                            style={{
                              padding: '0.5rem 1rem',
                              background: '#10b981',
                              color: 'white',
                              border: 'none',
                              borderRadius: '6px',
                              fontSize: '0.85rem',
                              fontWeight: '600',
                              cursor: 'pointer'
                            }}
                          >
                            Promote to Production
                          </button>
                        )}
                        {version.stage !== 'production' && (
                          <button
                            onClick={() => handleDeleteVersion(version.id, version.version)}
                            style={{
                              padding: '0.5rem 1rem',
                              background: '#ef4444',
                              color: 'white',
                              border: 'none',
                              borderRadius: '6px',
                              fontSize: '0.85rem',
                              fontWeight: '600',
                              cursor: 'pointer'
                            }}
                            title="Delete this version permanently"
                          >
                            🗑️ Delete
                          </button>
                        )}
                      </div>
                    </div>

                    {/* Agent Name Display */}
                    {(version as any).agent_name && (
                      <div style={{ marginBottom: '0.75rem' }}>
                        <div style={{ fontSize: '0.85rem', fontWeight: '600', color: '#6b7280', marginBottom: '0.25rem' }}>
                          🤖 Agent Name:
                        </div>
                        <div style={{
                          fontSize: '0.85rem',
                          color: '#374151',
                          padding: '0.5rem 0.75rem',
                          background: '#f9fafb',
                          borderRadius: '6px',
                          border: '1px solid #e5e7eb'
                        }}>
                          {(version as any).agent_name}
                        </div>
                      </div>
                    )}

                    <div style={{ marginBottom: '0.75rem' }}>
                      <div style={{ fontSize: '0.85rem', fontWeight: '600', color: '#6b7280', marginBottom: '0.25rem' }}>
                        System Prompt:
                      </div>
                      <div style={{
                        fontSize: '0.85rem',
                        color: '#374151',
                        padding: '0.75rem',
                        background: '#fff',
                        borderRadius: '6px',
                        border: '1px solid #e5e7eb',
                        whiteSpace: 'pre-wrap',
                        maxHeight: '150px',
                        overflowY: 'auto'
                      }}>
                        {version.system_prompt}
                      </div>
                    </div>

                    {version.changelog && (
                      <div style={{ marginBottom: '0.75rem' }}>
                        <div style={{ fontSize: '0.85rem', fontWeight: '600', color: '#6b7280', marginBottom: '0.25rem' }}>
                          Changelog:
                        </div>
                        <div style={{ fontSize: '0.85rem', color: '#6b7280' }}>
                          {version.changelog}
                        </div>
                      </div>
                    )}

                    {(version.temperature !== undefined || version.llm_model) && (
                      <>
                        {version.temperature !== undefined && version.temperature !== null && (
                          <div style={{ marginBottom: '0.75rem' }}>
                            <div style={{ fontSize: '0.85rem', fontWeight: '600', color: '#6b7280', marginBottom: '0.25rem' }}>
                              🌡️ Temperature:
                            </div>
                            <div style={{ fontSize: '0.85rem', color: '#374151', fontWeight: '600' }}>
                              {Number(version.temperature).toFixed(2)}
                            </div>
                          </div>
                        )}

                        {version.llm_model && (
                          <div style={{ marginBottom: '0.75rem' }}>
                            <div style={{ fontSize: '0.85rem', fontWeight: '600', color: '#6b7280', marginBottom: '0.25rem' }}>
                              🤖 LLM Model:
                            </div>
                            <div style={{
                              fontSize: '0.85rem',
                              color: '#374151',
                              padding: '0.5rem',
                              background: '#f0f9ff',
                              borderRadius: '4px',
                              border: '1px solid #bae6fd',
                              fontWeight: '600'
                            }}>
                              {version.llm_model}
                            </div>
                          </div>
                        )}

                        {/* Show typing settings if available */}
                        {(version.typing_speed_cps !== undefined || version.typing_burst_enabled !== undefined) && (
                          <div style={{ marginBottom: '0.75rem' }}>
                            <div style={{ fontSize: '0.85rem', fontWeight: '600', color: '#6b7280', marginBottom: '0.25rem' }}>
                              ⏱️ Human-like Timing:
                            </div>
                            <div style={{ fontSize: '0.85rem', color: '#374151' }}>
                              {version.typing_speed_cps && (
                                <span style={{ fontWeight: '600' }}>
                                  {parseFloat(version.typing_speed_cps as string).toFixed(2)} CPS ({(parseFloat(version.typing_speed_cps as string) * 60 / 5).toFixed(0)} WPM)
                                </span>
                              )}
                              {version.typing_burst_enabled !== undefined && (
                                <span style={{ marginLeft: '0.5rem', color: '#6b7280' }}>
                                  • {version.typing_burst_enabled ? '💬 Burst ON' : 'Burst OFF'}
                                </span>
                              )}
                            </div>
                          </div>
                        )}

                        {/* Show old delay settings if available (for backward compatibility) */}
                        {(!version.typing_speed_cps && (version.min_response_delay_seconds !== undefined || version.max_response_delay_seconds !== undefined)) && (
                          <div style={{ marginBottom: '0.75rem' }}>
                            <div style={{ fontSize: '0.85rem', fontWeight: '600', color: '#6b7280', marginBottom: '0.25rem' }}>
                              ⏱️ Response Delay (Legacy):
                            </div>
                            <div style={{ fontSize: '0.85rem', color: '#374151', fontWeight: '600' }}>
                              {version.min_response_delay_seconds || 3}s - {version.max_response_delay_seconds || 7}s
                            </div>
                          </div>
                        )}
                      </>
                    )}

                    <div style={{ fontSize: '0.75rem', color: '#9ca3af' }}>
                      Created: {new Date(version.created_at).toLocaleDateString()} by {version.created_by || 'Unknown'}
                      {version.promoted_to_production_at && (
                        <> • Promoted: {new Date(version.promoted_to_production_at).toLocaleDateString()}</>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Quick Test Section on Main Page */}
            {versions.length > 0 && (
              <div style={{
                background: '#fff',
                padding: '1.5rem',
                borderRadius: '12px',
                boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                marginTop: '1.5rem'
              }}>
                <div style={{ marginBottom: '1rem' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                    <h3 style={{ margin: 0, fontSize: '1.15rem', color: '#1f2937', fontWeight: '700' }}>
                      🧪 Quick Test Agent
                    </h3>
                    {versions.length > 0 && selectedTestVersionId && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <select
                          value={selectedTestVersionId}
                          onChange={(e) => {
                            setSelectedTestVersionId(e.target.value);
                            setMainPageTestMessages([]);
                            setMainPageTestInput('');
                            setMainPageTestError(null);
                          }}
                          style={{
                            padding: '0.4rem 0.6rem',
                            border: '1px solid #d1d5db',
                            borderRadius: '6px',
                            fontSize: '0.85rem',
                            background: 'white',
                            cursor: 'pointer',
                            fontWeight: '500'
                          }}
                        >
                          {versions
                            .filter(v => v.stage === 'production' || v.stage === 'draft')
                            .sort((a, b) => b.version - a.version)
                            .map((v) => (
                              <option key={v.id} value={v.id}>
                                {v.stage === 'production' ? '🚀 ' : '📝 '}Version {v.version} ({v.stage})
                              </option>
                            ))}
                        </select>
                        {(() => {
                          const versionToTest = versions.find(v => v.id === selectedTestVersionId);
                          if (!versionToTest) return null;
                          return (
                            <span style={{
                              fontSize: '0.8rem',
                              padding: '0.2rem 0.5rem',
                              borderRadius: '4px',
                              background: versionToTest.stage === 'production' ? '#d1fae5' : '#f1f5f9',
                              color: versionToTest.stage === 'production' ? '#065f46' : '#475569',
                              fontWeight: '600'
                            }}>
                              {getStageIcon(versionToTest.stage)} {versionToTest.stage}
                            </span>
                          );
                        })()}
                      </div>
                    )}
                  </div>
                  <p style={{ fontSize: '0.85rem', color: '#6b7280', margin: 0 }}>
                    Test your agent with the selected version. Choose between production and draft versions.
                  </p>
                </div>

                {/* Test Messages History */}
                {mainPageTestMessages.length > 0 && (
                  <div style={{
                    marginBottom: '1rem',
                    maxHeight: '300px',
                    overflowY: 'auto',
                    padding: '1rem',
                    background: '#f9fafb',
                    borderRadius: '8px',
                    border: '1px solid #e5e7eb'
                  }}>
                    {mainPageTestMessages.map((msg, idx) => (
                      <div
                        key={idx}
                        style={{
                          marginBottom: idx < mainPageTestMessages.length - 1 ? '1rem' : 0,
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: msg.role === 'user' ? 'flex-end' : 'flex-start'
                        }}
                      >
                        <div style={{
                          maxWidth: '80%',
                          padding: '0.75rem 1rem',
                          borderRadius: '12px',
                          background: msg.role === 'user' ? '#f97316' : '#f3f4f6',
                          color: msg.role === 'user' ? 'white' : '#1f2937',
                          fontSize: '0.9rem',
                          lineHeight: '1.5',
                          wordBreak: 'break-word'
                        }}>
                          {msg.content}
                        </div>
                        {msg.role === 'assistant' && msg.confidence !== undefined && (
                          <div style={{
                            fontSize: '0.75rem',
                            color: '#6b7280',
                            marginTop: '0.35rem',
                            marginLeft: '0.5rem',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.35rem'
                          }}>
                            <span>Confidence:</span>
                            <span style={{
                              padding: '0.15rem 0.5rem',
                              borderRadius: '4px',
                              fontWeight: '600',
                              background: msg.confidence >= 0.7 ? '#d1fae5' : msg.confidence >= 0.4 ? '#fef3c7' : '#fee2e2',
                              color: msg.confidence >= 0.7 ? '#065f46' : msg.confidence >= 0.4 ? '#92400e' : '#991b1b'
                            }}>
                              {(msg.confidence * 100).toFixed(0)}%
                            </span>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}

                {/* Test Input */}
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  <input
                    type="text"
                    value={mainPageTestInput}
                    onChange={(e) => setMainPageTestInput(e.target.value)}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter' && mainPageTestInput.trim() && !mainPageTesting) {
                        handleMainPageTest();
                      }
                    }}
                    disabled={mainPageTesting}
                    placeholder="Type a test message to test your agent..."
                    style={{
                      flex: 1,
                      padding: '0.75rem 1rem',
                      border: '1px solid #d1d5db',
                      borderRadius: '8px',
                      fontSize: '0.9rem',
                      opacity: mainPageTesting ? 0.6 : 1
                    }}
                  />
                  <button
                    onClick={handleMainPageTest}
                    disabled={!mainPageTestInput.trim() || mainPageTesting}
                    style={{
                      padding: '0.75rem 1.25rem',
                      background: mainPageTestInput.trim() && !mainPageTesting ? '#2563eb' : '#d1d5db',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      fontSize: '0.9rem',
                      fontWeight: '600',
                      cursor: mainPageTestInput.trim() && !mainPageTesting ? 'pointer' : 'not-allowed',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem'
                    }}
                  >
                    {mainPageTesting ? (
                      <>
                        <span style={{ animation: 'spin 1s linear infinite' }}>⏳</span>
                        Testing...
                      </>
                    ) : (
                      <>
                        <span>Send</span>
                        <span>➤</span>
                      </>
                    )}
                  </button>
                  {mainPageTestMessages.length > 0 && (
                    <button
                      onClick={() => {
                        setMainPageTestMessages([]);
                        setMainPageTestInput('');
                        setMainPageTestError(null);
                      }}
                      style={{
                        padding: '0.75rem 1rem',
                        background: '#fee2e2',
                        color: '#991b1b',
                        border: '1px solid #fecaca',
                        borderRadius: '8px',
                        fontSize: '0.85rem',
                        fontWeight: '600',
                        cursor: 'pointer'
                      }}
                    >
                      Clear
                    </button>
                  )}
                </div>

                {mainPageTestError && (
                  <div style={{
                    marginTop: '0.75rem',
                    padding: '0.75rem',
                    background: '#fee2e2',
                    color: '#991b1b',
                    borderRadius: '6px',
                    fontSize: '0.85rem'
                  }}>
                    ⚠️ {mainPageTestError}
                  </div>
                )}
              </div>
            )}
          </div>
        ) : (
          <div style={{ padding: '2rem', textAlign: 'center', color: '#6b7280' }}>
            Select an agent to view details
          </div>
        )}
      </div>

      {/* Create Agent Modal */}
      {showCreateModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: 'white',
            padding: '2rem',
            borderRadius: '12px',
            width: '500px',
            maxHeight: '80vh',
            overflowY: 'auto'
          }}>
            <h3 style={{ margin: '0 0 1.5rem 0', fontSize: '1.25rem' }}>Create Chat Agent</h3>

            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600', fontSize: '0.9rem' }}>
                Agent Name *
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  border: '1px solid #e5e7eb',
                  borderRadius: '6px',
                  fontSize: '0.9rem'
                }}
                placeholder="e.g., Customer Support Bot"
              />
            </div>

            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600', fontSize: '0.9rem' }}>
                Description
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  border: '1px solid #e5e7eb',
                  borderRadius: '6px',
                  fontSize: '0.9rem',
                  minHeight: '80px',
                  resize: 'vertical'
                }}
                placeholder="Brief description of this agent's purpose"
              />
            </div>

            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600', fontSize: '0.9rem' }}>
                Business Domain
              </label>
              <input
                type="text"
                value={formData.business_domain}
                onChange={(e) => setFormData({ ...formData, business_domain: e.target.value })}
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  border: '1px solid #e5e7eb',
                  borderRadius: '6px',
                  fontSize: '0.9rem'
                }}
                placeholder="e.g., immigration, ecommerce, healthcare"
              />
            </div>

            <div style={{ marginBottom: '1.5rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600', fontSize: '0.9rem' }}>
                Default Language
              </label>
              <select
                value={formData.default_language}
                onChange={(e) => setFormData({ ...formData, default_language: e.target.value })}
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  border: '1px solid #e5e7eb',
                  borderRadius: '6px',
                  fontSize: '0.9rem'
                }}
              >
                <option value="en">English</option>
                <option value="es">Spanish</option>
                <option value="fr">French</option>
                <option value="de">German</option>
                <option value="zh">Chinese</option>
              </select>
            </div>

            {error && (
              <div style={{
                padding: '0.75rem',
                background: '#fee2e2',
                color: '#991b1b',
                borderRadius: '6px',
                marginBottom: '1rem',
                fontSize: '0.9rem'
              }}>
                {error}
              </div>
            )}

            <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end' }}>
              <button
                onClick={() => {
                  setShowCreateModal(false);
                  setError(null);
                }}
                style={{
                  padding: '0.75rem 1.5rem',
                  background: '#f3f4f6',
                  color: '#374151',
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: '0.9rem',
                  fontWeight: '600',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleCreateAgent}
                disabled={!formData.name}
                style={{
                  padding: '0.75rem 1.5rem',
                  background: formData.name ? '#f97316' : '#d1d5db',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: '0.9rem',
                  fontWeight: '600',
                  cursor: formData.name ? 'pointer' : 'not-allowed'
                }}
              >
                Create Agent
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create Version Modal */}
      {showVersionModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: 'white',
            borderRadius: '12px',
            width: '900px',
            maxHeight: '85vh',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column'
          }}>
            <div style={{
              padding: '2rem 2rem 0 2rem',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '1.5rem'
            }}>
              <h3 style={{ margin: 0, fontSize: '1.25rem' }}>
                {versionModalSource.mode === 'edit' ? '✏️ Edit Version' : '🆕 Create New Version'}
              </h3>
              <button
                onClick={() => {
                  setShowVersionModal(false);
                  setShowExplainer(true);
                }}
                style={{
                  background: '#eff6ff',
                  color: '#2563eb',
                  border: '1px solid #bfdbfe',
                  borderRadius: '6px',
                  padding: '0.5rem 1rem',
                  fontSize: '0.85rem',
                  fontWeight: '600',
                  cursor: 'pointer'
                }}
              >
                📖 How do AI Agents work?
              </button>
            </div>

            <div style={{ padding: '0 2rem 1rem 2rem' }}>
              <div style={{
                padding: '0.75rem 1rem',
                borderRadius: '8px',
                background: versionModalSource.mode === 'edit' ? '#eff6ff' : '#fff7ed',
                border: `1px solid ${versionModalSource.mode === 'edit' ? '#bfdbfe' : '#fed7aa'}`,
                color: versionModalSource.mode === 'edit' ? '#1d4ed8' : '#9a3412',
                fontSize: '0.85rem',
                fontWeight: '500'
              }}>
                {versionModalSource.mode === 'edit'
                  ? `Editing version ${editingVersion?.version ?? ''} directly. Save will update this selected version.`
                  : versionModalSource.sourceVersion
                    ? `Creating a new draft based on version ${versionModalSource.sourceVersion.version} (${versionModalSource.sourceVersion.stage}).`
                    : 'Creating a brand new draft version for this agent.'}
              </div>
            </div>

            <div style={{ flex: 1, overflowY: 'auto', padding: '0 2rem' }}>
            {/* Agent Name Field */}
            <div style={{ marginBottom: '1.5rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600', fontSize: '0.9rem' }}>
                🤖 Agent Name *
              </label>
              <input
                type="text"
                value={versionFormData.agent_name}
                onChange={(e) => setVersionFormData({ ...versionFormData, agent_name: e.target.value })}
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  border: '1px solid #e5e7eb',
                  borderRadius: '6px',
                  fontSize: '0.9rem'
                }}
                placeholder="Enter agent name (e.g., Ava, Emma, Alex)"
              />
              <p style={{ marginTop: '0.5rem', fontSize: '0.8rem', color: '#6b7280' }}>
                This name will be used when the agent introduces itself to customers
              </p>
            </div>

            <div style={{ marginBottom: '1rem' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                <label style={{ fontWeight: '600', fontSize: '0.9rem', margin: 0 }}>
                  System Prompt *
                </label>
                <button
                  onClick={() => setPromptExpanded(!promptExpanded)}
                  style={{
                    padding: '0.4rem 0.75rem',
                    background: '#f3f4f6',
                    color: '#374151',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '0.75rem',
                    fontWeight: '600',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.4rem'
                  }}
                >
                  <span>{promptExpanded ? '↕️' : '⬍'}</span>
                  <span>{promptExpanded ? 'Collapse' : 'Expand'}</span>
                </button>
              </div>
              <textarea
                value={versionFormData.system_prompt}
                onChange={(e) => setVersionFormData({ ...versionFormData, system_prompt: e.target.value })}
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  border: '1px solid #e5e7eb',
                  borderRadius: '6px',
                  fontSize: '0.9rem',
                  minHeight: promptExpanded ? '500px' : '200px',
                  resize: 'vertical',
                  fontFamily: 'monospace',
                  transition: 'min-height 0.2s ease'
                }}
                placeholder="Enter the system prompt for this agent version..."
              />

              <div style={{ marginTop: '0.75rem', display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
                <button
                  onClick={handleImprovePrompt}
                  disabled={!versionFormData.system_prompt || aiImproving}
                  style={{
                    padding: '0.5rem 1rem',
                    background: versionFormData.system_prompt && !aiImproving ? '#8b5cf6' : '#d1d5db',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    fontSize: '0.85rem',
                    fontWeight: '600',
                    cursor: versionFormData.system_prompt && !aiImproving ? 'pointer' : 'not-allowed',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem'
                  }}
                >
                  {aiImproving ? (
                    <>
                      <span>⏳</span>
                      <span>Improving...</span>
                    </>
                  ) : (
                    <>
                      <span>✨</span>
                      <span>Improve System Prompt with AI</span>
                    </>
                  )}
                </button>
                {hasCustomApiKey && availableModels.length > 0 ? (
                  <select
                    value={selectedModel}
                    onChange={(e) => setSelectedModel(e.target.value)}
                    disabled={aiImproving}
                    style={{
                      padding: '0.5rem',
                      border: '1px solid #d1d5db',
                      borderRadius: '6px',
                      fontSize: '0.85rem',
                      cursor: aiImproving ? 'not-allowed' : 'pointer',
                      background: 'white'
                    }}
                  >
                    {availableModels.map((model) => (
                      <option key={model.id} value={model.id}>
                        {model.name}
                      </option>
                    ))}
                  </select>
                ) : (
                  <span style={{ fontSize: '0.75rem', color: '#6b7280' }}>
                    Model: {selectedModel === 'deepseek/deepseek-v3.2' ? 'GLM-4.7 (Free)' : selectedModel}
                  </span>
                )}
              </div>
            </div>

            {aiSuggestion && (
              <div style={{
                marginBottom: '1rem',
                padding: '1rem',
                background: '#f0fdf4',
                border: '2px solid #86efac',
                borderRadius: '8px'
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
                  <h4 style={{ margin: 0, color: '#16a34a', fontSize: '0.95rem', fontWeight: '600' }}>
                    ✨ AI Suggested Improvement
                  </h4>
                  <span style={{ fontSize: '0.75rem', color: '#16a34a' }}>
                    {selectedModel === 'deepseek/deepseek-v3.2' ? 'GLM-4.7' : selectedModel}
                  </span>
                </div>

                <div style={{
                  background: 'white',
                  padding: '0.75rem',
                  borderRadius: '6px',
                  marginBottom: '0.75rem',
                  maxHeight: '300px',
                  overflowY: 'auto',
                  fontFamily: 'monospace',
                  fontSize: '0.85rem',
                  lineHeight: '1.6',
                  whiteSpace: 'pre-wrap',
                  border: '1px solid #d1fae5'
                }}>
                  {aiSuggestion}
                </div>

                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  <button
                    onClick={handleAcceptSuggestion}
                    style={{
                      padding: '0.5rem 1rem',
                      background: '#16a34a',
                      color: 'white',
                      border: 'none',
                      borderRadius: '6px',
                      fontSize: '0.85rem',
                      fontWeight: '600',
                      cursor: 'pointer',
                      flex: 1
                    }}
                  >
                    ✓ Accept
                  </button>
                  <button
                    onClick={handleRejectSuggestion}
                    style={{
                      padding: '0.5rem 1rem',
                      background: '#dc2626',
                      color: 'white',
                      border: 'none',
                      borderRadius: '6px',
                      fontSize: '0.85rem',
                      fontWeight: '600',
                      cursor: 'pointer',
                      flex: 1
                    }}
                  >
                    ✗ Reject
                  </button>
                </div>
              </div>
            )}

            <div style={{ marginBottom: '1.5rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600', fontSize: '0.9rem' }}>
                Changelog
              </label>
              <textarea
                value={versionFormData.changelog}
                onChange={(e) => setVersionFormData({ ...versionFormData, changelog: e.target.value })}
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  border: '1px solid #e5e7eb',
                  borderRadius: '6px',
                  fontSize: '0.9rem',
                  minHeight: '80px',
                  resize: 'vertical'
                }}
                placeholder="What changed in this version?"
              />
            </div>

            <div style={{ marginBottom: '1.5rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600', fontSize: '0.9rem' }}>
                🤖 LLM model for chatting
              </label>
              {availableModels.length > 0 ? (
                <select
                  value={versionFormData.llm_model}
                  onChange={(e) => setVersionFormData({ ...versionFormData, llm_model: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: '1px solid #e5e7eb',
                    borderRadius: '6px',
                    fontSize: '0.9rem',
                    background: 'white'
                  }}
                >
                  {availableModels.map((model) => (
                    <option key={model.id} value={model.id}>
                      {model.name}
                    </option>
                  ))}
                </select>
              ) : (
                <select
                  value={versionFormData.llm_model}
                  onChange={(e) => setVersionFormData({ ...versionFormData, llm_model: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: '1px solid #e5e7eb',
                    borderRadius: '6px',
                    fontSize: '0.9rem',
                    background: 'white'
                  }}
                >
                  <option value="deepseek/deepseek-v3.2">deepseek/deepseek-v3.2 (Recommended - Free)</option>
                  <option value="x-ai/grok-4.20">x-ai/grok-4.20</option>
                  <option value="anthropic/claude-3.5-sonnet">anthropic/claude-3.5-sonnet</option>
                  <option value="openai/gpt-4o-mini">openai/gpt-4o-mini</option>
                  <option value="google/gemini-3-flash-preview">google/gemini-3-flash-preview</option>
                  <option value="google/gemini-2.5-flash-preview">google/gemini-2.5-flash-preview</option>
                </select>
              )}
              <p style={{ fontSize: '0.75rem', color: '#6b7280', margin: '0.5rem 0 0 0' }}>
                Select the LLM model for this agent. {availableModels.length > 0 ? `${availableModels.length} models available.` : 'Loading models...'} Models with higher quality may have API costs.
              </p>
            </div>

            {/* Reasoning Setting */}
            <div style={{ marginBottom: '1.5rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600', fontSize: '0.9rem' }}>
                🧠 Reasoning Mode (for DeepSeek models)
              </label>
              <select
                value={versionFormData.reasoning === null ? 'auto' : versionFormData.reasoning === true ? 'enable' : 'disable'}
                onChange={(e) => {
                  const value = e.target.value;
                  setVersionFormData({
                    ...versionFormData,
                    reasoning: value === 'auto' ? null : value === 'enable' ? true : false
                  });
                }}
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  border: '1px solid #e5e7eb',
                  borderRadius: '6px',
                  fontSize: '0.9rem',
                  background: 'white'
                }}
              >
                <option value="auto">Auto-detect (enable for DeepSeek models)</option>
                <option value="enable">Force Enable (for all models)</option>
                <option value="disable">Force Disable (even for DeepSeek)</option>
              </select>
              <p style={{ fontSize: '0.75rem', color: '#6b7280', margin: '0.5rem 0 0 0' }}>
                Reasoning mode enables DeepSeek's chain-of-thought reasoning for more accurate responses. Auto-detect will enable it for DeepSeek models only.
              </p>
            </div>

            {checkingApiKey && (
              <div style={{ marginBottom: '1.5rem', padding: '1rem', background: '#fef3c7', border: '1px solid #f59e0b', borderRadius: '8px', fontSize: '0.85rem', color: '#92400e' }}>
                ⏳ Checking OpenRouter API key...
              </div>
            )}
            {!hasOpenRouterKey && !checkingApiKey && (
              <div style={{ marginBottom: '1.5rem', padding: '1rem', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '8px', fontSize: '0.85rem', color: '#991b1b' }}>
                ⚠️ <strong>No OpenRouter API key configured.</strong> Please add an API Key in Admin → Integrations → OpenRouter Settings before creating an agent.
              </div>
            )}

            <div style={{ marginBottom: '1.5rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600', fontSize: '0.9rem' }}>
                Temperature (Creativity vs Accuracy)
              </label>
              <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                <input
                  type="range"
                  min="0"
                  max="1"
                  step="0.05"
                  value={versionFormData.temperature}
                  onChange={(e) => setVersionFormData({ ...versionFormData, temperature: parseFloat(e.target.value) })}
                  style={{ flex: 1 }}
                />
                <span style={{ fontWeight: '600', color: '#374151', minWidth: '40px' }}>
                  {versionFormData.temperature.toFixed(2)}
                </span>
              </div>
              <p style={{ fontSize: '0.75rem', color: '#6b7280', margin: '0.5rem 0 0 0' }}>
                Lower = More accurate/deterministic (0.05 recommended). Higher = More creative (up to 1.0)
              </p>
            </div>

            {/* Response Delay Settings */}
            <div style={{ marginBottom: '1.5rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600', fontSize: '0.9rem' }}>
                ⏱️ Response Delay (Human-like Timing)
              </label>

              {/* Typing Speed Setting */}
              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.85rem', color: '#374151', marginBottom: '0.5rem', fontWeight: '500' }}>
                  ⌨️ Typing Speed (Characters Per Second)
                  <span style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    padding: '0.15rem 0.4rem',
                    background: '#fef3c7',
                    borderRadius: '4px',
                    fontSize: '0.75rem',
                    color: '#92400e',
                    cursor: 'help'
                  }} title="Higher CPS = faster responses. 10 CPS = 2.5 min for 500 chars. 15 CPS = 1.7 min for 500 chars. 20 CPS = 1.3 min for 500 chars.">
                    ℹ️
                  </span>
                </label>
                <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                  <input
                    type="number"
                    min="5"
                    max="50"
                    step="1"
                    value={versionFormData.typing_speed_cps}
                    onChange={(e) => setVersionFormData({ ...versionFormData, typing_speed_cps: parseFloat(e.target.value) || 5 })}
                    style={{
                      width: '120px',
                      padding: '0.5rem',
                      border: '1px solid #e5e7eb',
                      borderRadius: '6px',
                      fontSize: '0.9rem'
                    }}
                  />
                  <span style={{ fontSize: '0.85rem', color: '#6b7280' }}>
                    CPS = <strong>{(Number(versionFormData.typing_speed_cps) * 60 / 5).toFixed(0)} WPM</strong>
                    {(() => {
                      const cps = Number(versionFormData.typing_speed_cps);
                      if (cps < 10) {
                        return <span style={{ color: '#92400e', marginLeft: '0.5rem' }}>(Slow: ~2.5 min/500 chars)</span>;
                      } else if (cps >= 10 && cps < 20) {
                        return <span style={{ color: '#059669', marginLeft: '0.5rem' }}>(Fast: ~1-2 min/500 chars)</span>;
                      } else {
                        return <span style={{ color: '#0284c7', marginLeft: '0.5rem' }}>(Very Fast: ~1 min/500 chars)</span>;
                      }
                    })()}
                  </span>
                </div>
                <p style={{ fontSize: '0.75rem', color: '#6b7280', margin: '0.5rem 0 0 0' }}>
                  Response time is calculated based on message length: <code style={{ background: '#f3f4f6', padding: '0.15rem 0.3rem', borderRadius: '3px' }}>delay = character_count ÷ CPS + thinking_time + pauses</code>
                </p>
              </div>

              {/* Typing Burst Simulation */}
              <div style={{
                padding: '1rem',
                background: '#fef3c7',
                border: '1px solid #fde68a',
                borderRadius: '6px',
                marginBottom: '0.5rem'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.75rem' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.85rem', color: '#374151', fontWeight: '500' }}>
                    💬 Typing Burst Simulation
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={versionFormData.typing_burst_enabled}
                      onChange={(e) => setVersionFormData({ ...versionFormData, typing_burst_enabled: e.target.checked })}
                      style={{ cursor: 'pointer' }}
                    />
                    <span style={{ fontSize: '0.85rem', color: '#374151' }}>Enabled</span>
                  </label>
                </div>
                <p style={{ fontSize: '0.75rem', color: '#92400e', margin: '0 0 0.75rem 0' }}>
                  When enabled, long responses are split into 2-3 shorter messages sent in sequence, simulating natural typing bursts like a human would do.
                </p>
                {versionFormData.typing_burst_enabled && (
                  <div style={{ display: 'flex', gap: '1rem' }}>
                    <div style={{ flex: 1 }}>
                      <label style={{ fontSize: '0.8rem', color: '#6b7280', display: 'block', marginBottom: '0.25rem' }}>
                        Min Chunks
                      </label>
                      <input
                        type="number"
                        min="1"
                        max="5"
                        value={versionFormData.typing_burst_min}
                        onChange={(e) => setVersionFormData({ ...versionFormData, typing_burst_min: Math.min(parseInt(e.target.value) || 1, Number(versionFormData.typing_burst_max) || 3) })}
                        style={{
                          width: '100%',
                          padding: '0.5rem',
                          border: '1px solid #e5e7eb',
                          borderRadius: '6px',
                          fontSize: '0.9rem'
                        }}
                      />
                    </div>
                    <div style={{ flex: 1 }}>
                      <label style={{ fontSize: '0.8rem', color: '#6b7280', display: 'block', marginBottom: '0.25rem' }}>
                        Max Chunks
                      </label>
                      <input
                        type="number"
                        min="2"
                        max="5"
                        value={versionFormData.typing_burst_max}
                        onChange={(e) => setVersionFormData({ ...versionFormData, typing_burst_max: Math.max(parseInt(e.target.value) || 3, Number(versionFormData.typing_burst_min) || 1) })}
                        style={{
                          width: '100%',
                          padding: '0.5rem',
                          border: '1px solid #e5e7eb',
                          borderRadius: '6px',
                          fontSize: '0.9rem'
                        }}
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Formatting Rules Section */}
            <div style={{
              marginBottom: '1.5rem',
              padding: '1rem',
              background: '#f0fdf4',
              border: '1px solid #bbf7d0',
              borderRadius: '8px'
            }}>
              <label style={{ display: 'block', marginBottom: '0.75rem', fontWeight: '600', fontSize: '0.9rem' }}>
                📝 Response Formatting Rules
              </label>
              <p style={{ fontSize: '0.8rem', color: '#6b7280', marginBottom: '1rem', lineHeight: '1.6' }}>
                Control how the agent formats its responses to ensure clean, consistent output.
              </p>

              {/* Max Questions Per Response */}
              <div style={{ marginBottom: '1rem' }}>
                <label style={{ fontSize: '0.8rem', color: '#6b7280', display: 'block', marginBottom: '0.25rem' }}>
                  ❓ Max Questions Per Response
                </label>
                <input
                  type="number"
                  min="1"
                  max="10"
                  value={versionFormData.max_questions_per_response}
                  onChange={(e) => setVersionFormData({ ...versionFormData, max_questions_per_response: parseInt(e.target.value) || 1 })}
                  style={{
                    width: '120px',
                    padding: '0.5rem',
                    border: '1px solid #e5e7eb',
                    borderRadius: '6px',
                    fontSize: '0.9rem'
                  }}
                />
                <p style={{ fontSize: '0.75rem', color: '#6b7280', margin: '0.5rem 0 0 0' }}>
                  Maximum number of questions the agent can ask in a single message (default: 2)
                </p>
              </div>

              {/* Strip Markdown Bold Toggle */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1rem' }}>
                <input
                  type="checkbox"
                  checked={versionFormData.strip_markdown_bold}
                  onChange={(e) => setVersionFormData({ ...versionFormData, strip_markdown_bold: e.target.checked })}
                  style={{
                    width: '18px',
                    height: '18px',
                    cursor: 'pointer'
                  }}
                />
                <div style={{ flex: 1 }}>
                  <label style={{ fontSize: '0.85rem', fontWeight: '600', color: '#374151', cursor: 'pointer' }}>
                    🚫 No Markdown Bold Formatting (Prompt Rule)
                  </label>
                  <p style={{ fontSize: '0.75rem', color: '#6b7280', margin: '0.25rem 0 0 0' }}>
                    Tell the LLM not to use **bold** markdown in its responses (recommended: ON)
                  </p>
                </div>
              </div>

              {/* Enable Post-Processing Toggle */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                <input
                  type="checkbox"
                  checked={versionFormData.enable_post_processing}
                  onChange={(e) => setVersionFormData({ ...versionFormData, enable_post_processing: e.target.checked })}
                  style={{
                    width: '18px',
                    height: '18px',
                    cursor: 'pointer'
                  }}
                />
                <div style={{ flex: 1 }}>
                  <label style={{ fontSize: '0.85rem', fontWeight: '600', color: '#374151', cursor: 'pointer' }}>
                    🛡️ Force Strip Markdown (Post-Processing Safety Net)
                  </label>
                  <p style={{ fontSize: '0.75rem', color: '#6b7280', margin: '0.25rem 0 0 0' }}>
                    Forcibly remove markdown even if LLM violates instructions - uses regex to strip **bold**, *italic* after response (recommended: ON)
                  </p>
                </div>
              </div>
            </div>

            {/* Client Memory Configuration */}
            <div style={{
              marginBottom: '1.5rem',
              padding: '1rem',
              background: '#fef3c7',
              border: '1px solid #fde68a',
              borderRadius: '8px'
            }}>
              <label style={{ display: 'block', marginBottom: '0.75rem', fontWeight: '600', fontSize: '0.9rem' }}>
                🧠 Client Memory Configuration
              </label>
              <p style={{ fontSize: '0.8rem', color: '#92400e', marginBottom: '1rem', lineHeight: '1.6' }}>
                Configure what information the agent should extract and remember from conversations. This helps the agent remember client details like name, phone, email, etc.
              </p>

              {/* Enable/Disable Client Memory */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1rem' }}>
                <input
                  type="checkbox"
                  checked={versionFormData.client_memory_config?.enabled ?? true}
                  onChange={(e) => setVersionFormData({
                    ...versionFormData,
                    client_memory_config: {
                      ...versionFormData.client_memory_config,
                      enabled: e.target.checked,
                      fields: versionFormData.client_memory_config?.fields || []
                    }
                  })}
                  style={{
                    width: '18px',
                    height: '18px',
                    cursor: 'pointer'
                  }}
                />
                <div style={{ flex: 1 }}>
                  <label style={{ fontSize: '0.85rem', fontWeight: '600', color: '#374151', cursor: 'pointer' }}>
                    ✅ Enable Client Memory Extraction
                  </label>
                  <p style={{ fontSize: '0.75rem', color: '#92400e', margin: '0.25rem 0 0 0' }}>
                    When enabled, the agent will extract and remember client information from the conversation
                  </p>
                </div>
              </div>

              {/* Fields List */}
              {versionFormData.client_memory_config?.enabled !== false && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                  {(versionFormData.client_memory_config?.fields || []).map((field: any, index: number) => (
                    <div
                      key={index}
                      style={{
                        padding: '0.75rem',
                        background: '#ffffff',
                        border: '1px solid #e5e7eb',
                        borderRadius: '6px',
                        position: 'relative'
                      }}
                    >
                      {/* Field Header */}
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.75rem' }}>
                        <input
                          type="checkbox"
                          checked={field.enabled ?? true}
                          onChange={(e) => {
                            const newFields = [...(versionFormData.client_memory_config?.fields || [])];
                            newFields[index] = { ...newFields[index], enabled: e.target.checked };
                            setVersionFormData({
                              ...versionFormData,
                              client_memory_config: {
                                ...versionFormData.client_memory_config,
                                fields: newFields
                              }
                            });
                          }}
                          style={{ width: '16px', height: '16px', cursor: 'pointer' }}
                        />
                        <input
                          type="text"
                          value={field.label || ''}
                          onChange={(e) => {
                            const newFields = [...(versionFormData.client_memory_config?.fields || [])];
                            newFields[index] = { ...newFields[index], label: e.target.value };
                            setVersionFormData({
                              ...versionFormData,
                              client_memory_config: {
                                ...versionFormData.client_memory_config,
                                fields: newFields
                              }
                            });
                          }}
                          placeholder="Field Label (e.g., Name, Phone)"
                          style={{
                            flex: 1,
                            padding: '0.4rem 0.5rem',
                            border: '1px solid #e5e7eb',
                            borderRadius: '4px',
                            fontSize: '0.85rem',
                            fontWeight: '600'
                          }}
                        />
                        <select
                          value={(field as any).field_type || 'identity'}
                          onChange={(e) => {
                            const newFields: any[] = [...(versionFormData.client_memory_config?.fields || [])];
                            newFields[index] = { ...newFields[index], field_type: e.target.value };
                            setVersionFormData({
                              ...versionFormData,
                              client_memory_config: {
                                ...versionFormData.client_memory_config,
                                fields: newFields
                              }
                            });
                          }}
                          style={{
                            padding: '0.4rem 0.5rem',
                            border: '1px solid #e5e7eb',
                            borderRadius: '4px',
                            fontSize: '0.8rem',
                            background: (field as any).field_type === 'intake_fact' ? '#dbeafe' : '#f3f4f6'
                          }}
                          title="Field category"
                        >
                          <option value="identity">👤 Identity</option>
                          <option value="intake_fact">📋 Intake Fact</option>
                        </select>
                        <select
                          value={field.extraction_type || 'regex'}
                          onChange={(e) => {
                            const newFields: any[] = [...(versionFormData.client_memory_config?.fields || [])];
                            newFields[index] = {
                              ...newFields[index],
                              extraction_type: e.target.value,
                              ...(e.target.value === 'keyword' ? { keywords: {}, patterns: undefined, llm_prompt: undefined } :
                                  e.target.value === 'structured_llm' ? { llm_prompt: '', patterns: undefined, keywords: undefined } :
                                  { patterns: [], keywords: undefined, llm_prompt: undefined })
                            };
                            setVersionFormData({
                              ...versionFormData,
                              client_memory_config: {
                                ...versionFormData.client_memory_config,
                                fields: newFields
                              }
                            });
                          }}
                          style={{
                            padding: '0.4rem 0.5rem',
                            border: '1px solid #e5e7eb',
                            borderRadius: '4px',
                            fontSize: '0.8rem'
                          }}
                        >
                          <option value="regex" title="Use regex patterns to extract structured data (e.g., phone numbers, email addresses). Fast and free, but limited to specific patterns.">Regex Pattern</option>
                          <option value="keyword" title="Match specific keywords in conversation (e.g., 'ltvp' → 'LTVP'). Simple and free, but may miss variations.">Keyword Matching</option>
                          <option value="structured_llm" title="🤖 Use LLM to understand context and extract information. Most accurate for complex data like names, relationships, and natural language. Cost: ~$0.00003 per extraction (3 cents per 1000 messages)">🤖 Structured LLM</option>
                        </select>
                        <button
                          onClick={() => {
                            const newFields = [...(versionFormData.client_memory_config?.fields || [])];
                            newFields.splice(index, 1);
                            setVersionFormData({
                              ...versionFormData,
                              client_memory_config: {
                                ...versionFormData.client_memory_config,
                                fields: newFields
                              }
                            });
                          }}
                          style={{
                            padding: '0.3rem 0.6rem',
                            background: '#ef4444',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            fontSize: '0.75rem',
                            fontWeight: '600',
                            cursor: 'pointer'
                          }}
                          title="Delete this field"
                        >
                          🗑️ Delete
                        </button>
                      </div>

                      {/* Field Key */}
                      <div style={{ marginBottom: '0.5rem' }}>
                        <label style={{ fontSize: '0.75rem', color: '#6b7280', display: 'block', marginBottom: '0.25rem' }}>
                          Field Key (internal identifier, e.g., name, phone, email)
                        </label>
                        <input
                          type="text"
                          value={field.key || ''}
                          onChange={(e) => {
                            const newFields = [...(versionFormData.client_memory_config?.fields || [])];
                            newFields[index] = { ...newFields[index], key: e.target.value };
                            setVersionFormData({
                              ...versionFormData,
                              client_memory_config: {
                                ...versionFormData.client_memory_config,
                                fields: newFields
                              }
                            });
                          }}
                          placeholder="field_key"
                          style={{
                            width: '100%',
                            padding: '0.4rem 0.5rem',
                            border: '1px solid #e5e7eb',
                            borderRadius: '4px',
                            fontSize: '0.85rem'
                          }}
                        />
                      </div>

                      {/* Patterns or Keywords or LLM Prompt */}
                      {field.extraction_type === 'regex' ? (
                        <div>
                          <label style={{ fontSize: '0.75rem', color: '#6b7280', display: 'block', marginBottom: '0.25rem' }}>
                            Regex Patterns (one per line)
                          </label>
                          <textarea
                            value={(field.patterns || []).join('\n')}
                            onChange={(e) => {
                              const newFields = [...(versionFormData.client_memory_config?.fields || [])];
                              newFields[index] = {
                                ...newFields[index],
                                patterns: e.target.value.split('\n').filter(p => p.trim())
                              };
                              setVersionFormData({
                                ...versionFormData,
                                client_memory_config: {
                                  ...versionFormData.client_memory_config,
                                  fields: newFields
                                }
                              });
                            }}
                            placeholder="My name is {name}&#10;I am {name}&#10;\\+?\\d{8,15}"
                            style={{
                              width: '100%',
                              padding: '0.5rem',
                              border: '1px solid #e5e7eb',
                              borderRadius: '4px',
                              fontSize: '0.85rem',
                              minHeight: '60px',
                              fontFamily: 'monospace'
                            }}
                          />
                          <p style={{ fontSize: '0.7rem', color: '#92400e', margin: '0.25rem 0 0 0' }}>
                            Use {'{name}'} for capture groups (e.g., "My name is {'{name}'}"). Supports standard regex.
                          </p>
                        </div>
                      ) : field.extraction_type === 'structured_llm' ? (
                        <div>
                          <label style={{ fontSize: '0.75rem', color: '#6b7280', display: 'block', marginBottom: '0.25rem' }}>
                            🤖 LLM Extraction Prompt
                          </label>
                          <textarea
                            value={(field as any).llm_prompt || ''}
                            onChange={(e) => {
                              const newFields: any[] = [...(versionFormData.client_memory_config?.fields || [])];
                              newFields[index] = { ...newFields[index], llm_prompt: e.target.value };
                              setVersionFormData({
                                ...versionFormData,
                                client_memory_config: {
                                  ...versionFormData.client_memory_config,
                                  fields: newFields
                                }
                              });
                            }}
                            placeholder="Extract the nationality or country of origin of the applicant. Look for mentions like 'Filipino', 'Indian', 'Chinese', 'Malaysian'. Return only the nationality as a string."
                            style={{
                              width: '100%',
                              padding: '0.5rem',
                              border: '1px solid #e5e7eb',
                              borderRadius: '4px',
                              fontSize: '0.85rem',
                              minHeight: '80px'
                            }}
                          />
                          <p style={{ fontSize: '0.7rem', color: '#0891b2', margin: '0.25rem 0 0 0' }}>
                            💡 Structured LLM reliably extracts facts from natural language (e.g., "I'm on EP" → current_pass_type: "EP"). Better for complex intake facts than regex/keywords.
                          </p>
                        </div>
                      ) : (
                        <div>
                          <label style={{ fontSize: '0.75rem', color: '#6b7280', display: 'block', marginBottom: '0.25rem' }}>
                            Keywords (format: keyword=value, one per line)
                          </label>
                          <textarea
                            value={Object.entries((field as any).keywords || {}).map(([k, v]) => `${k}=${v}`).join('\n')}
                            onChange={(e) => {
                              const keywords: Record<string, string> = {};
                              e.target.value.split('\n').forEach(line => {
                                const [key, ...valueParts] = line.split('=');
                                if (key && valueParts.length > 0) {
                                  keywords[key.trim()] = valueParts.join('=').trim();
                                }
                              });
                              const newFields: any[] = [...(versionFormData.client_memory_config?.fields || [])];
                              newFields[index] = { ...newFields[index], keywords };
                              setVersionFormData({
                                ...versionFormData,
                                client_memory_config: {
                                  ...versionFormData.client_memory_config,
                                  fields: newFields
                                }
                              });
                            }}
                            placeholder="ltvp=LTVP&#10;work pass=Work Pass&#10;video call=Prefers video call"
                            style={{
                              width: '100%',
                              padding: '0.5rem',
                              border: '1px solid #e5e7eb',
                              borderRadius: '4px',
                              fontSize: '0.85rem',
                              minHeight: '60px',
                              fontFamily: 'monospace'
                            }}
                          />
                          <p style={{ fontSize: '0.7rem', color: '#92400e', margin: '0.25rem 0 0 0' }}>
                            When keyword is found in message, the corresponding value will be stored.
                          </p>
                        </div>
                      )}
                    </div>
                  ))}

                  {/* Add More Field Button */}
                  <button
                    onClick={() => {
                      const newFields = [...(versionFormData.client_memory_config?.fields || []), {
                        key: `field_${Date.now()}`,
                        label: 'New Field',
                        enabled: true,
                        field_type: 'identity',
                        patterns: [''],
                        extraction_type: 'regex'
                      }];
                      setVersionFormData({
                        ...versionFormData,
                        client_memory_config: {
                          ...versionFormData.client_memory_config,
                          fields: newFields
                        }
                      });
                    }}
                    style={{
                      padding: '0.5rem 1rem',
                      background: '#10b981',
                      color: 'white',
                      border: 'none',
                      borderRadius: '6px',
                      fontSize: '0.85rem',
                      fontWeight: '600',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem'
                    }}
                  >
                    ➕ Add More Field
                  </button>
                </div>
              )}
            </div>

            {/* Service Completion Rules */}
            <div style={{
              marginBottom: '1.5rem',
              padding: '1rem',
              background: '#f0f9ff',
              border: '1px solid #bae6fd',
              borderRadius: '8px'
            }}>
              <label style={{ display: 'block', marginBottom: '0.75rem', fontWeight: '600', fontSize: '0.9rem' }}>
                ✅ Service Completion Rules
              </label>
              <p style={{ fontSize: '0.8rem', color: '#075985', marginBottom: '1rem', lineHeight: '1.6' }}>
                Define which memory fields must be collected before allowing calendar booking for each service. This ensures minimum intake information is gathered.
              </p>

              {/* Note about configuration */}
              <div style={{
                padding: '0.75rem',
                background: '#fef3c7',
                border: '1px solid #fde68a',
                borderRadius: '6px',
                marginBottom: '1rem'
              }}>
                <p style={{ fontSize: '0.75rem', color: '#92400e', margin: 0, lineHeight: '1.5' }}>
                  💡 <strong>Configuration:</strong> Service completion rules are managed at the tenant level. Use the Admin panel → Services Config to edit which fields are required for each immigration service (PR, LTVP, Citizenship, etc.).
                </p>
              </div>

              {/* Example Display */}
              <div style={{ fontSize: '0.8rem', color: '#075985' }}>
                <p style={{ fontWeight: '600', marginBottom: '0.5rem' }}>Example Rules:</p>
                <ul style={{ marginTop: 0, paddingLeft: '1.5rem', lineHeight: '1.8' }}>
                  <li><strong>PR:</strong> nationality, pass_type, stay_length, employment, urgency (5 fields)</li>
                  <li><strong>LTVP:</strong> nationality, sponsor_status, relationship, urgency (4 fields)</li>
                  <li><strong>Denial of Entry:</strong> denial_date, checkpoint, nationality (3 fields)</li>
                </ul>
                <p style={{ fontSize: '0.75rem', color: '#0c4a6e', marginTop: '0.5rem' }}>
                  ℹ️ These rules are checked against extracted facts in <code style={{ background: '#e0f2fe', padding: '0.1rem 0.3rem', borderRadius: '3px' }}>client_memory_facts</code> table.
                </p>
              </div>
            </div>

            {/* Tool Selection */}
            <div style={{ marginBottom: '1.5rem' }}>
              <label style={{ display: 'block', marginBottom: '0.75rem', fontWeight: '600', fontSize: '0.9rem' }}>
                🔧 Tools & Capabilities
              </label>
              <p style={{ fontSize: '0.8rem', color: '#6b7280', marginBottom: '1rem', lineHeight: '1.6' }}>
                Select which tools this agent can use. Tools must be authenticated first (Admin → Integrations).
              </p>

              <div style={{ display: 'grid', gap: '0.75rem' }}>
                {availableTools.map((tool) => {
                  const toolId = tool.id as keyof typeof enabledTools;
                  const isAvailable = tool.status === 'available';
                  const isEnabled = enabledTools[toolId] || false;

                  return (
                    <div
                      key={tool.id}
                      style={{
                        display: 'flex',
                        alignItems: 'start',
                        padding: '1rem',
                        background: isAvailable ? '#f9fafb' : '#fef2f2',
                        border: `1px solid ${isAvailable ? '#e5e7eb' : '#fecaca'}`,
                        borderRadius: '8px',
                        opacity: isAvailable ? 1 : 0.7
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={isEnabled}
                        disabled={!isAvailable}
                        onChange={(e) => setEnabledTools({ ...enabledTools, [toolId]: e.target.checked })}
                        style={{
                          width: '18px',
                          height: '18px',
                          marginRight: '0.75rem',
                          marginTop: '0.1rem',
                          cursor: isAvailable ? 'pointer' : 'not-allowed'
                        }}
                      />
                      <div style={{ flex: 1 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.25rem' }}>
                          <strong style={{ fontSize: '0.9rem', color: '#1f2937' }}>{tool.name}</strong>
                          <span
                            style={{
                              fontSize: '0.7rem',
                              padding: '0.15rem 0.5rem',
                              borderRadius: '4px',
                              fontWeight: '600',
                              background: isAvailable ? '#d1fae5' : '#fee2e2',
                              color: isAvailable ? '#065f46' : '#991b1b'
                            }}
                          >
                            {isAvailable ? 'Available' : 'Not Authenticated'}
                          </span>
                        </div>
                        <p style={{ fontSize: '0.8rem', color: '#6b7280', margin: 0, lineHeight: '1.5' }}>
                          {tool.description}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>

              <div style={{
                marginTop: '1rem',
                padding: '0.75rem',
                background: '#eff6ff',
                borderRadius: '6px',
                border: '1px solid #bfdbfe'
              }}>
                <p style={{ fontSize: '0.75rem', color: '#1e40af', margin: 0, lineHeight: '1.6' }}>
                  💡 <strong>Tip:</strong> Enable only the tools your agent needs. RAG (Knowledge Base Search) and Human Handoff are recommended for most agents.
                </p>
              </div>
            </div>

            {/* Agent Testing Section */}
            <div style={{
              marginBottom: '1.5rem',
              padding: '1.25rem',
              background: '#eff6ff',
              borderRadius: '8px',
              border: '2px solid #3b82f6'
            }}>
              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '700', fontSize: '1rem', color: '#1e40af' }}>
                  🧪 Test Your Agent
                </label>
                <p style={{ fontSize: '0.85rem', color: '#1e40af', margin: 0, lineHeight: '1.5' }}>
                  Send test messages to see how your agent responds. Shows confidence scores for each response.
                </p>
              </div>

              {/* Test Messages History */}
              {testMessages.length > 0 && (
                <div style={{
                  marginBottom: '1rem',
                  maxHeight: '250px',
                  overflowY: 'auto',
                  padding: '0.75rem',
                  background: 'white',
                  borderRadius: '6px',
                  border: '1px solid #e5e7eb'
                }}>
                  {testMessages.map((msg, idx) => (
                    <div
                      key={idx}
                      style={{
                        marginBottom: idx < testMessages.length - 1 ? '0.75rem' : 0,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: msg.role === 'user' ? 'flex-end' : 'flex-start'
                      }}
                    >
                      <div style={{
                        maxWidth: '80%',
                        padding: '0.6rem 0.85rem',
                        borderRadius: '12px',
                        background: msg.role === 'user' ? '#f97316' : '#f3f4f6',
                        color: msg.role === 'user' ? 'white' : '#1f2937',
                        fontSize: '0.85rem',
                        lineHeight: '1.5',
                        wordBreak: 'break-word',
                        whiteSpace: 'pre-wrap'
                      }}>
                        {msg.content}
                      </div>
                      {msg.role === 'assistant' && msg.confidence !== undefined && (
                        <div style={{
                          fontSize: '0.7rem',
                          color: '#6b7280',
                          marginTop: '0.25rem',
                          marginLeft: '0.5rem',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.35rem'
                        }}>
                          <span>Confidence:</span>
                          <span style={{
                            padding: '0.1rem 0.4rem',
                            borderRadius: '4px',
                            fontWeight: '600',
                            background: msg.confidence >= 0.7 ? '#d1fae5' : msg.confidence >= 0.4 ? '#fef3c7' : '#fee2e2',
                            color: msg.confidence >= 0.7 ? '#065f46' : msg.confidence >= 0.4 ? '#92400e' : '#991b1b'
                          }}>
                            {(msg.confidence * 100).toFixed(0)}%
                          </span>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {/* Test Input */}
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <input
                  type="text"
                  value={testInput}
                  onChange={(e) => setTestInput(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter' && testInput.trim() && !testing) {
                      handleTestAgent();
                    }
                  }}
                  disabled={testing}
                  placeholder="Type a test message..."
                  style={{
                    flex: 1,
                    padding: '0.6rem 0.85rem',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '0.85rem',
                    opacity: testing ? 0.6 : 1
                  }}
                />
                <button
                  onClick={handleTestAgent}
                  disabled={!testInput.trim() || testing}
                  style={{
                    padding: '0.6rem 1rem',
                    background: testInput.trim() && !testing ? '#2563eb' : '#d1d5db',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    fontSize: '0.85rem',
                    fontWeight: '600',
                    cursor: testInput.trim() && !testing ? 'pointer' : 'not-allowed',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.35rem'
                  }}
                >
                  {testing ? (
                    <>
                      <span style={{ animation: 'spin 1s linear infinite' }}>⏳</span>
                      Testing...
                    </>
                  ) : (
                    <>
                      <span>Send</span>
                      <span>➤</span>
                    </>
                  )}
                </button>
                {testMessages.length > 0 && (
                  <button
                    onClick={() => {
                      setTestMessages([]);
                      setTestInput('');
                      setTestError(null);
                    }}
                    style={{
                      padding: '0.6rem 0.85rem',
                      background: '#fee2e2',
                      color: '#991b1b',
                      border: '1px solid #fecaca',
                      borderRadius: '6px',
                      fontSize: '0.8rem',
                      fontWeight: '600',
                      cursor: 'pointer'
                    }}
                  >
                    Clear
                  </button>
                )}
              </div>

              {testError && (
                <div style={{
                  marginTop: '0.75rem',
                  padding: '0.6rem 0.75rem',
                  background: '#fee2e2',
                  color: '#991b1b',
                  borderRadius: '6px',
                  fontSize: '0.8rem'
                }}>
                  ⚠️ {testError}
                </div>
              )}
            </div>

            {error && (
              <div style={{
                padding: '0.75rem',
                background: '#fee2e2',
                color: '#991b1b',
                borderRadius: '6px',
                marginBottom: '1rem',
                fontSize: '0.9rem'
              }}>
                {error}
              </div>
            )}

            {saveSuccess && (
              <div style={{
                padding: '0.75rem',
                background: '#d1fae5',
                color: '#065f46',
                borderRadius: '6px',
                marginBottom: '1rem',
                fontSize: '0.9rem'
              }}>
                {saveSuccess}
              </div>
            )}

            </div>

            <div style={{
              display: 'flex',
              gap: '0.75rem',
              justifyContent: 'space-between',
              padding: '1rem 2rem 2rem 2rem',
              borderTop: '1px solid #e5e7eb',
              background: 'white',
              position: 'sticky',
              bottom: 0
            }}>
              <div>
                {editingVersion && (
                  <button
                    onClick={() => {
                      setShowVersionModal(false);
                      setTimeout(() => handleCopyToNewVersion(editingVersion), 100);
                    }}
                    disabled={isSaving}
                    style={{
                      padding: '0.75rem 1.5rem',
                      background: isSaving ? '#d1d5db' : '#8b5cf6',
                      color: 'white',
                      border: 'none',
                      borderRadius: '6px',
                      fontSize: '0.9rem',
                      fontWeight: '600',
                      cursor: isSaving ? 'not-allowed' : 'pointer',
                      opacity: isSaving ? 0.6 : 1
                    }}
                    title="Copy all settings and prompt from this version to create a new version"
                  >
                    📋 Copy to New Version
                  </button>
                )}
              </div>
              <div style={{ display: 'flex', gap: '0.75rem' }}>
                <button
                  onClick={handleCloseModal}
                  disabled={isSaving}
                  style={{
                    padding: '0.75rem 1.5rem',
                    background: '#f3f4f6',
                    color: '#374151',
                    border: 'none',
                    borderRadius: '6px',
                    fontSize: '0.9rem',
                    fontWeight: '600',
                    cursor: isSaving ? 'not-allowed' : 'pointer',
                    opacity: isSaving ? 0.6 : 1
                  }}
                >
                  Cancel
                </button>
              <button
                onClick={handleCreateVersion}
                disabled={isSaving || !versionFormData.system_prompt}
                style={{
                  padding: '0.75rem 1.5rem',
                  background: (!isSaving && versionFormData.system_prompt) ? '#3b82f6' : '#d1d5db',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: '0.9rem',
                  fontWeight: '600',
                  cursor: (!isSaving && versionFormData.system_prompt) ? 'pointer' : 'not-allowed',
                  opacity: (isSaving || !versionFormData.system_prompt) ? 0.6 : 1,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}
              >
                {isSaving ? (
                  <>
                    <span style={{ display: 'inline-block', animation: 'spin 1s linear infinite' }}>⏳</span>
                    Saving...
                  </>
                ) : versionModalSource.mode === 'edit' ? (
                  <>Save</>
                ) : (
                  <>Create Version (Draft)</>
                )}
              </button>
              <button
                onClick={handleCreateVersionAndExit}
                disabled={isSaving || !versionFormData.system_prompt}
                style={{
                  padding: '0.75rem 1.5rem',
                  background: (!isSaving && versionFormData.system_prompt) ? '#10b981' : '#d1d5db',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: '0.9rem',
                  fontWeight: '600',
                  cursor: (!isSaving && versionFormData.system_prompt) ? 'pointer' : 'not-allowed',
                  opacity: (isSaving || !versionFormData.system_prompt) ? 0.6 : 1,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}
              >
                {isSaving ? (
                  <>
                    <span style={{ display: 'inline-block', animation: 'spin 1s linear infinite' }}>⏳</span>
                    Saving...
                  </>
                ) : versionModalSource.mode === 'edit' ? (
                  <>Save and Exit</>
                ) : (
                  <>Create and Exit</>
                )}
              </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Confirmation Dialog */}
      {showCloseConfirmation && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.6)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1001
        }}>
          <div style={{
            background: 'white',
            borderRadius: '12px',
            padding: '2rem',
            maxWidth: '450px',
            boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1), 0 10px 10px -5px rgba(0,0,0,0.04)'
          }}>
            <h3 style={{ margin: '0 0 1rem 0', fontSize: '1.25rem', color: '#1f2937' }}>
              ⚠️ Unsaved Changes
            </h3>
            <p style={{ margin: '0 0 1.5rem 0', color: '#6b7280', fontSize: '0.95rem', lineHeight: '1.5' }}>
              You have unsaved changes. Would you like to save as draft before exiting?
            </p>
            <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end' }}>
              <button
                onClick={handleDiscardChanges}
                style={{
                  padding: '0.75rem 1.5rem',
                  background: '#f3f4f6',
                  color: '#374151',
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: '0.9rem',
                  fontWeight: '600',
                  cursor: 'pointer'
                }}
              >
                Discard Changes
              </button>
              <button
                onClick={() => setShowCloseConfirmation(false)}
                style={{
                  padding: '0.75rem 1.5rem',
                  background: '#6b7280',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: '0.9rem',
                  fontWeight: '600',
                  cursor: 'pointer'
                }}
              >
                Continue Editing
              </button>
              <button
                onClick={handleSaveAsDraft}
                disabled={!versionFormData.system_prompt}
                style={{
                  padding: '0.75rem 1.5rem',
                  background: versionFormData.system_prompt ? '#f97316' : '#d1d5db',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: '0.9rem',
                  fontWeight: '600',
                  cursor: versionFormData.system_prompt ? 'pointer' : 'not-allowed'
                }}
              >
                Save as Draft
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
    </>
  );
};
